import { formatDate } from '@angular/common';
import { Component, DoCheck, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { defer, from, fromEvent, of, Subject, Subscription, timer } from 'rxjs';
import { exhaustMap, finalize, map, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { ProjectsService } from '../../../../../../../core/projects.service';
import {
  CreateReferenceLayerPayload,
  ProjectReferenceLayerService,
  ReferenceLayerSourceType,
  ReferenceLayerSummary,
  UpdateReferenceLayerPayload,
} from '../../../../../../../../../shared/services/project-reference-layer.service';
import {
  PROJECT_MAP_SOURCE_MAX_BYTES, PROJECT_MAP_SOURCE_MAX_MB, ProjectMapArcGISInspection, ProjectMapSourceAsset, ProjectMapSourceFormat, ProjectMapSourceService,
  ProjectMapSourceUnits, ProjectMapSourceVersion, ProjectMapUploadMetadata,
} from '../../../../../../../../../shared/services/project-map-source.service';
import { cornersToWebMercator } from '../../../../../../../../../shared/helpers/reference-layer-image';
import { distanceText, featureHighlightSymbol, geoJsonToArcGis, titleLookAlikes } from '../../../../../../../../../shared/helpers/project-map-inspection';
import { MapSourcePreviewFamily, mapSourcePreviewRenderer } from '../../../../../../../../../shared/helpers/map-source-preview-style';
import {
  MAX_SOURCE_COLOR_VALUES, SOURCE_COLOR_FIELD, SOURCE_COLOR_FIELD_LABEL, SOURCE_FILL_COLOR_FIELD, SOURCE_STYLE_FIELDS,
  sourceColorDefault, sourceColorLabel, sourceHexColor,
} from '../../../../../../../../../shared/helpers/source-color-defaults';

import { AuthService } from '../../../../../../../../../shared/services/auth.service';
import { ProjectMapManifest, ProjectMapStyle } from '../../../../../../../../../shared/services/project-map-manifest.service';
import { MAX_UNIQUE_VALUE_SYMBOLS, MapPreparedRendition, MapPublicationEntry, MapRenditionJob, MapRenditionSelection, MapReviewColorBy,
  MapReviewLayer, MapReviewSettings, ProjectMapPublishingService } from '../../../../../../../../../shared/services/project-map-publishing.service';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import { ProjectMapManifestService } from '../../../../../../../../../shared/services/project-map-manifest.service';
import { MAP_VIEW_BY_COLORS } from '../../../../../../../components/maps/map.constants';

// UI-level source-type options. 'kml_upload' is a UI concept — the service's
// upload() endpoint sets source_type='kml_url' on the server side.
type SourceTypeOption = ReferenceLayerSourceType | 'kml_upload' | 'dxf_upload' | 'pdf_upload' | 'source_import';

// 25 MB — matches the server-side guard for KML/KMZ upload size.
const MAX_KML_FILE_MB = 25;
const MAX_KML_FILE_SIZE_BYTES = MAX_KML_FILE_MB * 1024 * 1024;
const KML_ACCEPTED_EXTENSIONS = ['.kml', '.kmz'];
const IMPORT_FORMAT_EXTENSIONS: Record<ProjectMapSourceFormat, string[]> = {
  dxf: ['.dxf'], geojson: ['.geojson', '.json'], kml: ['.kml'], kmz: ['.kmz'], pdf: ['.pdf'],
};
// A field with more distinct values than this makes an unreadable map legend,
// so it is not offered as a color-by choice.
const MAX_COLOR_BY_VALUES = 24;
// How many of the import's listed fields a new layer's feature card starts with.
const DEFAULT_POPUP_FIELD_COUNT = 5;
// Preview basemaps. 'satellite' is the same well-known id the project map's
// Basemap tab uses, so the imagery a reviewer checks against matches the map.
const PREVIEW_BASEMAP_ID = 'gray-vector';
const PREVIEW_SATELLITE_BASEMAP_ID = 'satellite';
// Imagery hides thin linework, so the layer eases back when it is turned on.
const PREVIEW_SATELLITE_OPACITY = 0.6;
// Only used if a prepared rendition somehow carries no zoom levels; every real
// one does, because the server writes the range it built.
const FALLBACK_MIN_ZOOM = 12;
const FALLBACK_MAX_ZOOM = 16;

// Shape of the `data` payload accepted via the right-panel injection. Either
// a full ReferenceLayerSummary (edit mode) or `{ projectId }` (create mode
// when the caller — e.g. the fullscreen map toolbar — knows the project id
// but the dialog's own ActivatedRoute would resolve to the wrong route.
export type CreateOrEditMapLayerData =
  Partial<ReferenceLayerSummary> & { projectId?: string; sourceVersionId?: string; sourceFormat?: ProjectMapSourceFormat; groupId?: string | null; layerId?: string;
    /** When the design source was copied into SiteNotes (from the published catalog), for the provenance line. */
    sourceCapturedAt?: string;
    /** Opened from the row menu's "Upload/Capture new version": start the new-version flow once the published version loads. */
    newVersion?: boolean;
    /** With `groupId` not in the catalog yet: the group is created under that id with this name (a placed drawing: the API's "Drawings" group, W22). */
    groupName?: string };

interface PreviewFeatureCard {
  key: string; sublayer: string; label: string; fields: { label: string; value: string }[];
  /** Everything the feature carries that the popup could show, for telling look-alikes apart. */
  properties: Record<string, unknown>;
  /** Popup field names in the reviewer's order, for the same purpose. */
  fieldNames: string[];
  /** Screen distance from the click, for nearest-first ordering. */
  distance?: number;
  /** Metres from the click, shown under the name. */
  metres?: number;
  /** "Sublayer · 120 ft": the line under the title in the list. */
  meta?: string;
  /** Source preview: the drawn graphic (highlighted through its layer view). */
  graphic?: __esri.Graphic;
  /** Prepared preview: GeoJSON geometry from the lookup, outlined on a graphics layer. */
  geometry?: unknown;
}

/** Choices shown for one click; the rest are counted, not listed. */
const MAX_PREVIEW_CHOICES = 6;

/** Pixels from the click to the nearest point of a geometry drawn in the view. */
async function screenDistance(view: __esri.MapView, geometry: __esri.Geometry | null | undefined, event: { x: number; y: number }): Promise<number> {
  if (!geometry) return Number.POSITIVE_INFINITY;
  try {
    const [geometryEngine, webMercator] = await Promise.all([
      import('@arcgis/core/geometry/geometryEngine'), import('@arcgis/core/geometry/support/webMercatorUtils')]);
    const mapPoint = view.toMap(event);
    if (!mapPoint) return Number.POSITIVE_INFINITY;
    const inViewSr = geometry.spatialReference?.isWGS84 && mapPoint.spatialReference?.isWebMercator
      ? webMercator.geographicToWebMercator(geometry as __esri.GeometryUnion) as __esri.Geometry : geometry;
    const nearest = geometry.type === 'point' ? (inViewSr as __esri.Point) : geometryEngine.nearestCoordinate(inViewSr as __esri.GeometryUnion, mapPoint).coordinate;
    const screen = view.toScreen(nearest);
    return Math.hypot(screen.x - event.x, screen.y - event.y);
  } catch { return Number.POSITIVE_INFINITY; }
}

/** Metres per screen pixel at the click, from the view's resolution and latitude. */
function metresPerPixel(view: __esri.MapView, event: { x: number; y: number }): number {
  const mapPoint = view.toMap(event);
  const latitude = mapPoint?.latitude ?? 0;
  return view.resolution * Math.cos(latitude * Math.PI / 180);
}

@Component({
  selector: 'app-create-or-edit-map-layer',
  templateUrl: './create-or-edit-map-layer.component.html',
  styleUrls: ['./create-or-edit-map-layer.component.scss'],
  standalone: false,
})
export class CreateOrEditMapLayerComponent implements OnInit, OnChanges, OnDestroy, DoCheck {
  // Populated by projects-wrapper.createComponent(): `componentRef.instance.data = data`.
  @Input() data: CreateOrEditMapLayerData | null = null;

  projectId: string;
  newVersionSourceId = '';
  newVersionExtent: [number, number, number, number] | null = null;
  private newVersionLayerIds: number[] | null = null;
  @ViewChild('publicationPreview') previewElement?: ElementRef<HTMLDivElement>;
  reviewLayers: MapReviewLayer[] = [];
  reviewSettings: MapReviewSettings = { color: '#1036d6', strokeWidth: 2, pointSize: 8, fillAlpha: 0.35,
    popupFields: '', defaultVisible: false, defaultOpacity: 1, colorBy: null };
  /** Source properties the location preview found with few enough distinct values to color by. */
  fieldCandidates: { field: string; values: (string | number)[] }[] = [];
  /**
   * What the location preview learned about each colour the imported file drew
   * with: the CAD layers / KML folders that use it, the fills seen with it, and
   * how many features carry it. Keyed by lowercase `#rrggbb`.
   */
  private sourceColorMeta = new Map<string, { names: Map<string, number>; fills: Map<string, number>; count: number }>();
  /** True once this review defaulted itself to the colours the file authored. */
  private sourceColorsApplied = false;
  /** Shown when the file has more colours than one prepared layer can carry. */
  sourceColorHint = '';
  publicationCatalog: ProjectMapManifest | null = null;
  publicationBusy = false;
  publicationPhase = '';
  publicationError = '';
  publicationConflict = false;
  publicationLoaded = false;
  publicationRevision?: number;
  // Group for the published layers: an existing group id, '' to create `newGroupName`, or null for no group.
  groupId: string | null = null;
  newGroupName = '';
  layerOrder = 0;
  private reviewDefaultsScope = '';
  /** True once the settings were seeded from a published layer or a draft rendition. */
  private reviewSeeded = false;
  /** True once the reviewer changed a setting in this session; a draft never overwrites that. */
  private reviewDirty = false;
  /** What the drawer would publish when it opened, so a saved layer knows when it has edits. */
  private reviewBaseline = '';
  /** Every property name the location preview read from the source, for popup field checks. */
  sourceFieldNames: string[] = [];
  private legacyStyleWarning = '';
  preparedRenditions: MapPreparedRendition[] = [];
  renditionJob?: MapRenditionJob;
  reviewed = false;
  previewReady = false;
  previewOpen = false;
  previewExpired = false;
  previewRenderedCount = 0;
  previewOpacity = 1;
  previewExpanded = false;
  previewSatellite = false;
  private previewOpacityChosen = false;
  @ViewChild('previewShell') previewShellElement?: ElementRef<HTMLDivElement>;
  preparationNeedsRetry = false;
  /** True while a rendition is being prepared, and while layers are being published. */
  private preparing = false;
  private publishing = false;
  @ViewChild('sourcePreview') sourcePreviewElement?: ElementRef<HTMLDivElement>;
  sourcePreviewOpen = false;
  sourcePreviewBusy = false;
  sourcePreviewError = '';
  sourcePreviewFootprint = false;
  sourcePreviewSheetCount = 0;
  sourcePreviewOpacity = 0.8;
  sourcePreviewExpanded = false;
  sourcePreviewSatellite = false;
  private sourcePreviewOpacityChosen = false;
  @ViewChild('sourcePreviewShell') sourcePreviewShellElement?: ElementRef<HTMLDivElement>;
  /** The drawn source layers, kept so an appearance change re-styles them in place. */
  private sourcePreviewStyleLayers: { layer: __esri.GeoJSONLayer; family: MapSourcePreviewFamily }[] = [];
  private sourcePreviewView?: __esri.MapView;
  private livePreviewAbort?: AbortController;
  private livePreviewExtent?: __esri.Extent;
  private sourcePreviewUrls: string[] = [];
  private sourcePreviewExpires?: ReturnType<typeof setTimeout>;
  private previewAbort?: AbortController;
  private previewGeneration = 0;
  private publicationVersion = '';
  private publicationAbort = new AbortController();
  private publicationAccount = '';
  private publicationProject = '';
  private preparedSelections: MapRenditionSelection[] = [];
  private preparationRetry = 0;
  private previewView?: __esri.MapView;
  /** Tap-to-inspect in either preview: the features under the click, one chosen, its popup fields shown. */
  previewFeatures: PreviewFeatureCard[] | null = null;
  selectedPreviewFeature: PreviewFeatureCard | null = null;
  /** List of the features under the click, or one feature's details (mobile's two-level sheet). */
  previewFeatureView: 'list' | 'details' = 'list';
  /** Hits beyond the listed choices; the note asks the reviewer to zoom in. */
  previewFeaturesHidden = 0;
  private previewHighlightHandle?: IHandle;
  private previewHighlightGeneration = 0;
  private previewHighlightLayer?: __esri.GraphicsLayer;
  previewFeaturesNote = '';
  previewFeaturesBusy = false;
  private previewClickHandle?: IHandle;
  private sourcePreviewClickHandle?: IHandle;
  /** Full source properties per preview feature (the preview itself carries only colour-by fields). */
  private sourcePreviewProperties = new Map<number, { sublayer: string; properties: Record<string, unknown> }>();
  private previewExpires?: ReturnType<typeof setTimeout>;
  private newGroupId = '';


  name = '';
  sourceType: SourceTypeOption = 'arcgis_url';
  sourceUrl = '';
  arcgisMode: 'snapshot' | 'live' = 'snapshot';
  arcgisInspection: ProjectMapArcGISInspection | null = null;
  arcgisLayers: { id: number; name: string; selected: boolean; supported: boolean }[] = [];
  arcgisInspectBusy = false;
  arcgisError = '';
  private arcgisRequest?: Subscription;
  private arcgisGeneration = 0;
  private arcgisCaptureKey = '';
  private arcgisCaptureFingerprint = '';

  geojson: any = null;
  kmlFile: File | null = null;
  sourceFilename: string | null = null;
  dxfFile: File | null = null;
  dxfVersion: ProjectMapSourceVersion | null = null;
  sourceCrs = '';
  /** The numeric part of the EPSG code; the "EPSG:" prefix is fixed in the form. */
  get sourceCrsCode(): string { return this.sourceCrs.replace(/^EPSG:/i, ''); }
  set sourceCrsCode(value: string) {
    const digits = (value || '').replace(/[^0-9]/g, '').slice(0, 6);
    this.sourceCrs = digits ? `EPSG:${digits}` : '';
  }
  sourceUnits: ProjectMapSourceUnits | '' = '';
  importPhase = '';
  loadingVersion = false;
  pollingError = false;
  private readonly onDestroy = new Subject<void>();
  private readonly workCancelled = new Subject<void>();
  private workGeneration = 0;
  private initialized = false;
  private dxfRequest?: Subscription;
  private pollRequest?: Subscription;
  private uploadAbort?: AbortController;
  private fileReader?: FileReader;
  private dxfMetadata?: ProjectMapUploadMetadata;
  private dxfIdempotencyKey = '';
  private fileUploadComplete = false;
  fileError: string | null = null;
  submitting = false;
  submitError: string | null = null;
  // Captured on init in edit mode so canSubmit() can detect which fields the
  // user actually changed and submit() can build a minimal PATCH payload.
  private originalName = '';
  private originalSourceUrl = '';
  private originalSourceFilename: string | null = null;
  // Toggled while a file is being dragged over the dropzone so the SCSS can
  // surface a hover-style state. The native CSS :hover wouldn't trigger here
  // because the browser doesn't update :hover during a drag.
  isDragOver = false;

  constructor(
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private refLayerService: ProjectReferenceLayerService,
    public sourceService: ProjectMapSourceService,
    public publishingService: ProjectMapPublishingService,
    private manifestService: ProjectMapManifestService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.initializeData();
    this.initialized = true;
    fromEvent(document, 'fullscreenchange').pipe(takeUntil(this.onDestroy)).subscribe(() => {
      const wasSource = this.sourcePreviewExpanded; const wasPublication = this.previewExpanded;
      this.sourcePreviewExpanded = !!document.fullscreenElement && document.fullscreenElement === this.previewShell(true);
      this.previewExpanded = !!document.fullscreenElement && document.fullscreenElement === this.previewShell(false);
      // The view keeps the extent it had at the old size, so the layer sits small
      // in a fullscreen preview (or overflows a shrunk one). Refit once the map
      // has picked up the new container size.
      const refitSource = this.sourcePreviewExpanded || wasSource;
      const refitPublication = this.previewExpanded || wasPublication;
      if ((wasSource && !this.sourcePreviewExpanded) || (wasPublication && !this.previewExpanded)) this.closePreviewFeatures();
      if (refitSource || refitPublication) {
        setTimeout(() => { void this.zoomPreview(refitSource && !refitPublication); }, 250);
      }
    });
    // Fullscreen closes itself on Escape; the fallback branch (a browser without
    // the fullscreen API, or one that refused it) needs the same key to work.
    fromEvent<KeyboardEvent>(document, 'keydown').pipe(takeUntil(this.onDestroy)).subscribe(event => {
      if (event.key !== 'Escape' || document.fullscreenElement) return;
      if (!this.sourcePreviewExpanded && !this.previewExpanded) return;
      this.sourcePreviewExpanded = false;
      this.previewExpanded = false;
      this.closePreviewFeatures();
    });
    // Close an old project's panel when navigation changes its project context.
    for (const current of [this.route, this.route.parent].filter(Boolean)) {
      let previous = current.snapshot.paramMap.get('projectId');
      current.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(params => {
        const projectId = params.get('projectId');
        if (projectId && previous && projectId !== previous) {
          this.stopWork();
          this.closePanel();
        }
        previous = projectId;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes.data) this.initializeData();
  }

  ngDoCheck(): void {
    if (this.publicationVersion) this.currentPublication();
  }

  ngOnDestroy(): void {
    this.cancelPreviewUpdate();
    this.stopWork();
    this.onDestroy.next();
    this.onDestroy.complete();
    this.workCancelled.complete();
  }

  private initializeData(): void {
    this.removeSelectedFile();
    this.newVersionSourceId = '';
    this.newVersionExtent = null;
    this.newVersionLayerIds = null;
    this.name = '';
    this.sourceType = 'arcgis_url';
    this.arcgisMode = 'snapshot';
    this.sourceUrl = '';
    this.submitError = null;
    // Prefer the explicit projectId passed in via `data` — the fullscreen map
    // toolbar opens this panel from a route whose ActivatedRoute does NOT
    // expose the project's id (the URL-segment fallback there returned
    // 'fullscreen', not the uuid). Fall back to the route paramMap for the
    // Settings → Overlays entry point where the dialog mounts under
    // /project/:projectId/...
    this.projectId =
      this.data?.projectId ||
      this.route.snapshot.paramMap.get('projectId') ||
      this.route.snapshot.parent?.paramMap.get('projectId') ||
      '';

    if (this.data?.sourceVersionId) {
      this.name = this.data.name || '';
      this.sourceType = 'source_import';
      this.syncImportSourceType();
      this.startPolling(this.data.sourceVersionId);
      return;
    }

    if (this.data?.id) {
      this.name = this.data.name ?? '';
      this.originalName = this.name;
      this.arcgisMode = 'live';
      // Server-side source_type is 'arcgis_url' | 'geojson_upload' | 'kml_url',
      // but the UI radio uses 'kml_upload' for the KML option (matching the
      // server's upload route, not the column value). Map kml_url → kml_upload
      // so the right radio is selected in edit mode.
      const serverType = this.data.source_type;
      this.sourceType = serverType === 'kml_url' ? 'kml_upload' : (serverType ?? 'arcgis_url');
      this.sourceUrl = this.data.source_url ?? '';
      this.originalSourceUrl = this.sourceUrl;
      this.sourceFilename = this.data.source_filename ?? null;
      this.originalSourceFilename = this.sourceFilename;
      if (this.sourceType === 'arcgis_url' && this.sourceUrl) void this.showLivePreview();
    }
  }

  get isEdit(): boolean {
    return !!this.data?.id && !this.data?.sourceVersionId;
  }

  get canStartNewVersion(): boolean {
    return !!this.dxfVersion?.sourceId && this.dxfVersion.status === 'published'
      && !this.submitting && !this.publicationBusy && this.publishingService.canConfigure(this.projectId);
  }

  startNewVersion(): void {
    if (!this.canStartNewVersion) return;
    const version = this.dxfVersion;
    const format = this.importFormat;
    this.removeSelectedFile();
    this.newVersionSourceId = version.sourceId;
    this.newVersionExtent = version.arcgisSelection?.extent || null;
    this.newVersionLayerIds = version.arcgisSelection?.layerIds || null;
    this.data = { ...this.data, sourceVersionId: undefined, sourceFormat: format, newVersion: undefined };
    this.sourceUrl = version.sourceUrl || '';
    this.arcgisMode = 'snapshot';
    if (version.sourceKind === 'arcgis') this.sourceType = 'arcgis_url';
    else this.syncImportSourceType();
  }

  get isDesignImport(): boolean {
    return !!this.data?.sourceVersionId || !!this.dxfVersion || (!this.isEdit && ['dxf_upload', 'pdf_upload', 'geojson_upload', 'kml_upload'].includes(this.sourceType));
  }

  get importFormat(): ProjectMapSourceFormat | null {
    let format = this.dxfVersion?.format;
    if (!format && this.dxfVersion?.filename) {
      const extension = this.dxfVersion.filename.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
      format = (Object.keys(IMPORT_FORMAT_EXTENSIONS) as ProjectMapSourceFormat[])
        .find(candidate => IMPORT_FORMAT_EXTENSIONS[candidate].includes(extension));
    }
    format = format || this.data?.sourceFormat || (this.sourceType === 'dxf_upload' ? 'dxf' : this.sourceType === 'pdf_upload' ? 'pdf' : this.sourceType === 'geojson_upload' ? 'geojson'
      : this.sourceType === 'kml_upload' ? (this.dxfFile?.name.toLowerCase().endsWith('.kmz') ? 'kmz' : 'kml') : null);
    // A placed drawing has no file format of its own to upload or label.
    return format && format !== 'placed_plan' && IMPORT_FORMAT_EXTENSIONS[format] ? format : null;
  }

  get importFormatLabel(): string {
    if (this.dxfVersion?.sourceKind === 'arcgis') return 'ArcGIS snapshot';
    if (this.importFormat === 'pdf') return 'GeoPDF';
    return this.importFormat === 'geojson' ? 'GeoJSON' : this.importFormat?.toUpperCase() || 'Source';
  }

  /** "DXF · site.dxf · Copied Sep 20, 2026": where the reviewed version came from, in place of the source picker. */
  get provenanceLabel(): string {
    const copied = this.data?.sourceCapturedAt || this.dxfVersion?.capture?.captureInterval?.finishedAt;
    const timestamp = copied ? Date.parse(copied) : NaN;
    const parts = [this.importFormatLabel, this.sourceFilename, Number.isFinite(timestamp) ? `Copied ${formatDate(timestamp, 'mediumDate', 'en-US')}` : null];
    return parts.filter(Boolean).join(' · ');
  }

  get importNoun(): string {
    if (this.dxfVersion?.sourceKind === 'arcgis') return 'source';
    return this.importFormat === 'dxf' ? 'drawing' : this.importFormat === 'pdf' ? 'plan sheet' : 'file';
  }

  get designImportMaximumFileSizeMb(): number { return PROJECT_MAP_SOURCE_MAX_MB; }

  get sourceFileLabel(): string {
    if (this.isDesignImport) return `${this.importFormatLabel} ${this.importNoun}`;
    if (this.sourceType === 'kml_upload') return 'KML / KMZ file';
    if (this.sourceType === 'image_upload') return 'Image file';
    return 'GeoJSON file';
  }

  get sourceFileAccept(): string {
    if (this.isDesignImport) {
      const format = this.importFormat;
      return this.sourceType === 'kml_upload' && !this.dxfVersion ? '.kml,.kmz' : format ? IMPORT_FORMAT_EXTENSIONS[format].join(',') : '';
    }
    return this.sourceType === 'kml_upload' ? '.kml,.kmz' : '.json,.geojson,application/json,application/geo+json';
  }

  private syncImportSourceType(): void {
    const format = this.importFormat;
    this.sourceType = format === 'dxf' ? 'dxf_upload' : format === 'pdf' ? 'pdf_upload' : (format === 'geojson' ? 'geojson_upload'
      : (format === 'kml' || format === 'kmz' ? 'kml_upload' : 'source_import'));
  }

  /**
   * True when the layer's source file can be replaced via PATCH. The API
   * accepts a new geojson body for plain geojson_upload rows (no source_url),
   * but rejects geojson/source_filename mutations on rows that derive from a
   * stored archive (kml_url, or geojson_upload converted from KML — those
   * have a non-null source_url pointing at S3). For arcgis_url rows there's
   * no file to replace.
   */
  get canReplaceFile(): boolean {
    if (this.data?.sourceVersionId || this.dxfVersion?.id) return false;
    if (!this.isEdit) return true;
    return this.data?.source_type === 'geojson_upload' && !this.data?.source_url;
  }

  get canResumeUpload(): boolean {
    return this.isDesignImport && !!this.importFormat && this.dxfVersion?.status === 'uploaded'
      && !this.dxfVersion.uploaded && !this.fileUploadComplete;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.routeFile(file, input);
  }

  /** Add a hover state class while a file is dragged over the dropzone. */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  /** Clear the hover state when the cursor leaves the dropzone. */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  /**
   * Handle a file dropped onto the dropzone. Re-uses the same routing /
   * validation logic as the native <input type="file"> change event so
   * KML/KMZ vs GeoJSON gate-keeping stays in one place.
   */
  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.routeFile(file, null);
  }

  /**
   * Shared dispatcher: clears existing file state, then routes to the
   * KML- or GeoJSON-specific handler based on the current sourceType. The
   * `inputEl` argument lets handleKmlFile() reset the native input so the
   * user can re-pick the same file after fixing a validation error; for
   * dropped files there's no input element, so it's null.
   */
  private routeFile(file: File, inputEl: HTMLInputElement | null): void {
    if (this.submitting || (!this.canReplaceFile && !this.canResumeUpload)) return;
    const resuming = this.canResumeUpload;
    if (resuming) this.removeResumeFile();
    else this.removeSelectedFile();
    if (this.isDesignImport) {
      const format = this.importFormat;
      const extensions = this.sourceType === 'kml_upload' && !this.dxfVersion
        ? ['.kml', '.kmz'] : format ? IMPORT_FORMAT_EXTENSIONS[format] : [];
      if (!extensions.some(extension => file.name.toLowerCase().endsWith(extension))
          || !file.size || file.size > PROJECT_MAP_SOURCE_MAX_BYTES) {
        this.fileError = `Choose a ${this.importFormatLabel} ${this.importNoun} between 1 byte and ${this.designImportMaximumFileSizeMb} MB.`;
        if (inputEl) inputEl.value = '';
        return;
      }
      this.dxfFile = file;
      if (!resuming) {
        this.sourceFilename = file.name;
        if (!this.name.trim()) this.name = file.name.replace(/\.[^.]+$/, '');
      }
    } else if (this.sourceType === 'kml_upload') {
      this.handleKmlFile(file, inputEl);
    } else {
      this.handleGeojsonFile(file);
    }
  }

  removeSelectedFile(): void {
    this.stopWork();
    this.dxfFile = null;
    this.dxfVersion = null;
    this.dxfMetadata = undefined;
    this.dxfIdempotencyKey = '';
    this.fileUploadComplete = false;
    this.sourceCrs = '';
    this.sourceUnits = '';
    this.pollingError = false;
    this.geojson = null;
    this.kmlFile = null;
    this.sourceFilename = null;
    this.fileError = null;
  }

  removeResumeFile(): void {
    // Selecting the original again must retain the server's source/version identity.
    this.stopWork();
    this.dxfFile = null;
    this.fileError = null;
    this.submitError = null;
  }

  onSourceTypeChange(): void {
    // Clear file/URL state so switching source types doesn't carry stale data.
    this.removeSelectedFile();
    this.sourceUrl = '';
    this.submitError = null;
  }

  canSubmit(): boolean {
    if (!this.publishingService.canConfigure(this.projectId)) return false;
    if (!this.name.trim()) return false;
    if (this.fileError) return false;
    if (this.isDesignImport) {
      if (this.loadingVersion || this.importIsProcessing) return false;
      if (this.showLocationInputs) {
        return /^EPSG:[0-9]{3,6}$/.test(this.sourceCrs.trim().toUpperCase()) && !!this.sourceUnits
          && (this.dxfVersion?.status !== 'failed' || this.sourceCrs.trim().toUpperCase() !== this.dxfVersion.sourceCrs
            || this.sourceUnits !== this.dxfVersion.sourceUnits);
      }
      if (this.dxfVersion && !this.dxfVersion.uploaded && !this.fileUploadComplete) {
        return this.canResumeUpload && !!this.dxfFile;
      }
      if (this.dxfVersion) return this.dxfVersion.status === 'uploaded';
      return !!this.dxfFile;
    }
    if (this.isEdit) return this.hasEditChanges();
    if (this.sourceType === 'arcgis_url') return !!this.sourceUrl.trim() && (this.arcgisMode === 'live'
      || (!this.arcgisInspectBusy && !!this.arcgisInspection && this.arcgisLayers.some(layer => layer.selected && layer.supported)));
    if (this.sourceType === 'kml_upload') return !!this.kmlFile;
    return !!this.geojson;
  }

  /** True when the user has changed at least one editable field in edit mode. */
  private hasEditChanges(): boolean {
    if (this.name.trim() !== this.originalName.trim()) return true;
    if (this.sourceType === 'arcgis_url'
        && this.sourceUrl.trim() !== this.originalSourceUrl.trim()) {
      return true;
    }
    // canReplaceFile gates the file picker UI, so a non-null geojson here
    // means the user dropped in a replacement geojson on a plain
    // geojson_upload layer.
    if (this.canReplaceFile && this.geojson) return true;
    return false;
  }

  submit(): void {
    if (!this.canSubmit() || this.submitting) return;
    if (this.isDesignImport) {
      this.submitDxf();
      return;
    }
    if (!this.isEdit && this.sourceType === 'arcgis_url' && this.arcgisMode === 'snapshot') {
      this.captureArcGIS();
      return;
    }
    this.submitting = true;
    this.submitError = null;

    if (this.isEdit && this.data?.id) {
      const patch = this.buildEditPayload();
      this.refLayerService
        .update(this.projectId, this.data.id, patch)
        .pipe(takeUntil(this.workCancelled), takeUntil(this.onDestroy))
        .subscribe({
          next: () => this.onSubmitSuccess(),
          error: err => this.onSubmitError(err),
        });
      return;
    }

    if (this.sourceType === 'kml_upload' && this.kmlFile) {
      this.refLayerService
        .upload(this.projectId, this.kmlFile, this.name.trim())
        .pipe(takeUntil(this.workCancelled), takeUntil(this.onDestroy))
        .subscribe({
          next: () => this.onSubmitSuccess(),
          error: err => this.onSubmitError(err),
        });
      return;
    }

    const payload: CreateReferenceLayerPayload = {
      name: this.name.trim(),
      source_type: this.sourceType as ReferenceLayerSourceType,
    };
    if (this.sourceType === 'arcgis_url') {
      payload.source_url = this.sourceUrl.trim();
    } else {
      payload.geojson = this.geojson;
      payload.source_filename = this.sourceFilename;
    }

    this.refLayerService.create(this.projectId, payload)
      .pipe(takeUntil(this.workCancelled), takeUntil(this.onDestroy)).subscribe({
      next: () => this.onSubmitSuccess(),
      error: err => this.onSubmitError(err),
    });
  }

  /** Build a PATCH payload containing only the fields the user actually changed. */
  private buildEditPayload(): UpdateReferenceLayerPayload {
    const patch: UpdateReferenceLayerPayload = {};
    if (this.name.trim() !== this.originalName.trim()) {
      patch.name = this.name.trim();
    }
    if (this.sourceType === 'arcgis_url'
        && this.sourceUrl.trim() !== this.originalSourceUrl.trim()) {
      patch.source_url = this.sourceUrl.trim();
    }
    if (this.canReplaceFile && this.geojson) {
      patch.geojson = this.geojson;
      patch.source_filename = this.sourceFilename;
    }
    return patch;
  }

  private handleGeojsonFile(file: File): void {
    this.sourceFilename = file.name;
    const generation = this.workGeneration;
    const reader = new FileReader();
    this.fileReader = reader;
    reader.onload = () => {
      if (generation !== this.workGeneration) return;
      try {
        const parsed = JSON.parse(reader.result as string);
        if (parsed?.type !== 'FeatureCollection' || !Array.isArray(parsed.features)) {
          this.fileError = 'File is not a GeoJSON FeatureCollection.';
          return;
        }
        this.geojson = parsed;
      } catch {
        this.fileError = 'Could not parse file as JSON.';
      }
    };
    reader.onerror = () => {
      if (generation === this.workGeneration) this.fileError = 'The file could not be read.';
    };
    reader.readAsText(file);
  }

  private handleKmlFile(file: File, input: HTMLInputElement | null): void {
    const lowerName = file.name.toLowerCase();
    const hasValidExt = KML_ACCEPTED_EXTENSIONS.some(ext => lowerName.endsWith(ext));
    if (!hasValidExt) {
      this.fileError = 'Please select a .kml or .kmz file.';
      // Reset the native input so the user can re-pick the same file after
      // fixing the issue (browsers suppress change events for identical picks).
      // No-op for drag-and-drop where there's no input element.
      if (input) input.value = '';
      return;
    }
    if (file.size > MAX_KML_FILE_SIZE_BYTES) {
      this.fileError = `File is too large. Maximum size is ${MAX_KML_FILE_MB} MB.`;
      if (input) input.value = '';
      return;
    }
    this.kmlFile = file;
    this.sourceFilename = file.name;

    // If the user hasn't typed a name yet, default it to the filename
    // (without extension) as a helpful starting point.
    if (!this.name.trim()) {
      const dot = file.name.lastIndexOf('.');
      this.name = dot > 0 ? file.name.slice(0, dot) : file.name;
    }
  }

  get importNameLocked(): boolean {
    return this.isDesignImport && !!this.dxfMetadata;
  }

  get importIsProcessing(): boolean {
    return ['inspecting', 'processing'].includes(this.dxfVersion?.status || '');
  }

  get showLocationInputs(): boolean {
    return this.dxfVersion?.status === 'needs_input'
      || this.dxfVersion?.job?.failureCode === 'COORDINATE_TRANSFORM_UNAVAILABLE';
  }

  get importIsFinished(): boolean {
    return ['ready_for_review', 'published', 'withdrawn', 'failed', 'cancelled'].includes(this.dxfVersion?.status || '')
      && !this.showLocationInputs;
  }

  get importFeatureCount(): number {
    return (this.dxfVersion?.sublayers || []).reduce((sum, layer) => sum + layer.featureCount, 0);
  }

  get importImageCount(): number {
    return (this.dxfVersion?.sublayers || []).filter(layer => layer.geometryTypes.includes('RASTER')).length;
  }

  get hasSelectedVectors(): boolean {
    return this.reviewLayers.some(layer => layer.selected && this.dxfVersion?.assets.some(asset =>
      asset.sublayerKey === layer.key && asset.kind === 'geojson' && asset.role === 'geometry'));
  }

  get importActionLabel(): string {
    if (this.submitting) return this.importPhase || 'Working…';
    if (this.showLocationInputs) return 'Confirm location';
    if (this.importIsProcessing) return 'Processing…';
    if (this.canResumeUpload) return 'Resume upload';
    if (this.dxfVersion && (this.fileUploadComplete || !this.dxfFile)) return `Process ${this.importNoun}`;
    return this.dxfVersion ? 'Retry upload' : `Upload ${this.importNoun}`;
  }

  closePanel(): void {
    this.projectsService.closeRightPanel.next();
  }

  private stopWork(): void {
    this.clearArcGISInspection();
    this.destroySourcePreview();
    this.sourcePreviewBusy = false; this.sourcePreviewError = ''; this.sourcePreviewFootprint = false; this.sourcePreviewSheetCount = 0;
    this.preparationNeedsRetry = false;
    this.preparing = false;
    this.publishing = false;
    this.publicationAbort.abort();
    this.publicationAbort = new AbortController();
    this.destroyPublicationPreview();
    this.publicationVersion = '';
    this.publicationLoaded = false;
    this.publicationBusy = false;
    this.publicationPhase = '';
    this.publicationError = '';
    this.publicationConflict = false;
    this.publicationCatalog = null;
    this.publicationRevision = undefined;
    this.reviewLayers = [];
    this.preparedRenditions = [];
    this.preparedSelections = [];
    this.renditionJob = undefined;
    this.preparationRetry = 0;
    this.reviewSettings = { color: '#1036d6', strokeWidth: 2, pointSize: 8, fillAlpha: 0.35,
      popupFields: '', defaultVisible: false, defaultOpacity: 1, colorBy: null };
    this.fieldCandidates = [];
    this.sourceColorMeta = new Map(); this.sourceColorsApplied = false; this.sourceColorHint = '';
    this.sourceFieldNames = []; this.reviewSeeded = false; this.reviewDirty = false; this.reviewBaseline = '';
    this.groupId = null; this.newGroupName = ''; this.layerOrder = 0;
    this.reviewDefaultsScope = ''; this.legacyStyleWarning = '';
    this.workGeneration++;
    this.workCancelled.next();
    this.dxfRequest?.unsubscribe();
    this.pollRequest?.unsubscribe();
    this.uploadAbort?.abort();
    this.fileReader?.abort();
    this.fileReader = undefined;
    this.submitting = false;
    this.loadingVersion = false;
    this.importPhase = '';
  }

  private submitDxf(): void {
    const generation = this.workGeneration;
    const projectId = this.projectId;
    this.submitting = true;
    this.submitError = null;
    this.uploadAbort?.abort();
    this.uploadAbort = new AbortController();
    const signal = this.uploadAbort.signal;
    let request;
    if (this.dxfVersion && (this.fileUploadComplete || this.dxfVersion.uploaded)) {
      this.importPhase = 'Starting processing…';
      const location = this.showLocationInputs ? {
        sourceCrs: this.sourceCrs.trim().toUpperCase(), sourceUnits: this.sourceUnits as ProjectMapSourceUnits,
      } : {};
      request = this.sourceService.process(projectId, this.dxfVersion.id, location);
    } else {
      const file = this.dxfFile;
      const resumeVersionId = this.dxfVersion?.id;
      this.importPhase = `Preparing ${this.importNoun}…`;
      request = defer(() => this.sourceService.hashFile(file, signal)).pipe(
        switchMap(sha256 => {
          if (resumeVersionId) {
            this.importPhase = `Checking original ${this.importNoun}…`;
            return this.sourceService.resumeUpload(projectId, resumeVersionId, { bytes: file.size, sha256 });
          }
          if (!this.dxfMetadata) {
            this.dxfMetadata = { name: this.name.trim(), filename: file.name, bytes: file.size, sha256 };
            this.dxfIdempotencyKey = Array.from(crypto.getRandomValues(new Uint8Array(16)))
              .map(value => value.toString(16).padStart(2, '0')).join('');
          }
          this.importPhase = 'Starting upload…';
          return this.sourceService.stage(projectId, this.dxfMetadata, this.dxfIdempotencyKey, this.newVersionSourceId);
        }),
        tap(intent => this.acceptVersion(intent.version)),
        switchMap(intent => {
          if (intent.version.status !== 'uploaded' || intent.upload === null) return of(intent);
          this.importPhase = `Uploading ${this.importNoun}…`;
          return from(this.sourceService.uploadFile(file, intent.upload, signal)).pipe(map(() => intent));
        }),
        switchMap(intent => {
          // Another session may already have prepared this version with a different location.
          if (intent.version.status !== 'uploaded') return of({ version: intent.version });
          this.fileUploadComplete = true;
          this.importPhase = 'Starting processing…';
          return this.sourceService.process(projectId, intent.version.id);
        }),
      );
    }
    this.dxfRequest = request.pipe(
      takeUntil(this.workCancelled), takeUntil(this.onDestroy),
      finalize(() => {
        if (generation === this.workGeneration) {
          this.submitting = false;
          this.importPhase = '';
        }
      }),
    ).subscribe({
      next: result => {
        if (generation !== this.workGeneration) return;
        this.acceptVersion(result.version);
        // Tell the list a new import exists so it opens the Pending tab on it
        // while processing runs, instead of staying on an empty On the map tab.
        this.sourceService.listChanged.next({ sourceId: result.version.sourceId, focus: true });
        this.startPolling(result.version.id);
      },
      error: error => { if (generation === this.workGeneration) this.onSubmitError(error); },
    });
  }

  refreshImportStatus(): void {
    if (this.submitting) return;
    const versionId = this.dxfVersion?.id || this.data?.sourceVersionId;
    if (versionId) this.startPolling(versionId);
  }

  private startPolling(versionId: string): void {
    this.pollRequest?.unsubscribe();
    const generation = this.workGeneration;
    const projectId = this.projectId;
    this.loadingVersion = !this.dxfVersion;
    this.pollingError = false;
    this.submitError = null;
    this.pollRequest = timer(0, 2500).pipe(
      exhaustMap(() => this.sourceService.getVersion(projectId, versionId)),
      takeWhile(version => ['inspecting', 'processing'].includes(version.status), true),
      takeUntil(this.workCancelled), takeUntil(this.onDestroy),
    ).subscribe({
      next: version => {
        if (generation !== this.workGeneration) return;
        this.loadingVersion = false;
        this.acceptVersion(version);
        if (!this.importIsProcessing) this.sourceService.listChanged.next();
      },
      error: error => {
        if (generation !== this.workGeneration) return;
        this.loadingVersion = false;
        this.pollingError = true;
        this.onSubmitError(error);
      },
    });
  }

  private acceptVersion(version: ProjectMapSourceVersion): void {
    if (this.dxfVersion?.id !== version.id || this.dxfVersion?.status !== version.status) {
      this.sourceCrs = version.sourceCrs || '';
      this.sourceUnits = version.sourceUnits || '';
    }
    this.dxfVersion = version;
    this.syncImportSourceType();
    this.sourceFilename = version.filename || this.sourceFilename;
    if (!this.name) this.name = (version.filename || 'Source file').replace(/\.(dxf|geojson|json|kml|kmz)$/i, '');
    // Opened from the row menu: go straight into the new-version flow (before the review load marks the drawer busy).
    if (this.data?.newVersion && this.canStartNewVersion) this.startNewVersion();
    if (this.canReviewPublication && this.publicationVersion !== version.id) void this.loadPublicationReview();
  }

  /**
   * A one-sublayer import becomes exactly one map layer, so the Name field at
   * the top renames it (the per-sublayer field below is hidden). Multi-sublayer
   * imports name each map layer in the review rows instead.
   */
  get nameEditsMapLayer(): boolean {
    return this.canReviewPublication && this.publicationLoaded && this.reviewLayers.length === 1;
  }

  onNameChange(): void {
    if (!this.nameEditsMapLayer) return;
    // The publish fingerprint carries the name, so this alone enables Save; the
    // prepared preview stays valid because tiles do not depend on the name.
    this.reviewLayers[0].name = this.name;
  }

  /** Footer line when nothing is prepared: what the reviewer can do next. */
  get staleStatus(): { label: string; action: string } {
    if (this.isPublishedLayer && !this.drawerDirty) return { label: 'On the map as saved', action: 'Preview map' };
    if (this.reusesPublishedRenditions) return { label: 'Ready to save', action: 'Preview map' };
    if (this.isPublishedLayer) return { label: 'Preview is out of date', action: 'Update preview' };
    return { label: 'Preview not built yet', action: 'Build preview' };
  }

  get canReviewPublication(): boolean {
    return !!this.dxfVersion && ['ready_for_review', 'published'].includes(this.dxfVersion.status);
  }

  /**
   * A clean first import (ready for review, no warnings or required inputs, and
   * not a new version of a source already on the map) can be added in one step:
   * the map preview is prepared automatically and no review checkbox is shown.
   */
  get oneStepAdd(): boolean {
    return this.dxfVersion?.status === 'ready_for_review' && !this.newVersionSourceId
      && !this.dxfVersion.sourceLayerIds?.length && !this.showLocationInputs && !this.reviewWarnings.length;
  }

  /** Name for the group that a multi-sublayer import defaults to: the source file name without its extension. */
  get sourceGroupName(): string {
    const filename = this.dxfVersion?.filename || '';
    return (filename ? filename.replace(/\.[^.]+$/, '') : this.name.trim()).slice(0, 256);
  }

  /** Converter messages, deduplicated across the source version and its capture. */
  private get importMessages(): string[] {
    return [...new Set([...(this.dxfVersion?.warnings || []), ...(this.dxfVersion?.capture?.warnings || [])])];
  }

  /**
   * Informational notes about how the source was rendered. Every full-size plan
   * sheet carries one, and there is nothing for the user to do about it, so they
   * are shown in review without disabling one-step add.
   */
  get renderNotes(): string[] {
    return this.importMessages.filter(message => this.sourceService.isRenderNote(message));
  }

  get reviewWarnings(): string[] {
    const missing = this.publicationCatalog?.layers.filter(layer => this.dxfVersion?.sourceLayerIds?.includes(layer.id)
      && !this.dxfVersion.sublayers.some(item => item.key === layer.sublayerKey)).map(layer => layer.name) || [];
    return [...new Set([...this.importMessages.filter(message => !this.sourceService.isRenderNote(message)),
      ...(missing.length ? [`This version does not contain: ${missing.join(', ')}. Those existing layers will stay on the map.`] : []),
      ...(this.legacyStyleWarning ? [this.legacyStyleWarning] : [])])];
  }

  get preparedBytes(): number { return this.preparedRenditions.reduce((sum, asset) => sum + asset.bytes, 0); }

  /**
   * The zoom range the server prepared, read off the tiles themselves. The
   * publisher is no longer asked for one, so the preview constraints and the
   * published manifest entry both take the range the renditions were built with.
   */
  private preparedZoomRange(sublayerKey?: string): { minZoom: number; maxZoom: number } {
    const levels = this.preparedRenditions.filter(asset => asset.kind === 'raster_xyz' && asset.role === 'geometry'
      && (!sublayerKey || asset.sublayerKey === sublayerKey)
      && Number.isInteger(asset.minZoom) && Number.isInteger(asset.maxZoom));
    if (!levels.length) return { minZoom: FALLBACK_MIN_ZOOM, maxZoom: FALLBACK_MAX_ZOOM };
    return { minZoom: Math.min(...levels.map(asset => asset.minZoom)),
      maxZoom: Math.max(...levels.map(asset => asset.maxZoom)) };
  }

  /** Attribution for the status row: the credit itself, or that there is none to show. */
  get publicationAttributionLabel(): string {
    const layer = this.publicationCatalog?.layers.find(item => item.sourceVersionId === this.dxfVersion?.id);
    const attribution = (layer as { attribution?: string })?.attribution?.trim();
    return attribution ? `Attribution: ${attribution}` : 'Attribution unavailable';
  }

  /**
   * What the drawer's status row says about the prepared output.
   * `stale` covers both "never built" and "the settings moved on", which
   * `publicationConfigurationChanged()` already collapses by clearing the
   * prepared renditions.
   */
  get publicationStatus(): 'working' | 'saved' | 'ready' | 'failed' | 'stale' {
    if (this.publicationBusy) return 'working';
    if (this.publicationRevision !== undefined) return 'saved';
    if (this.preparedRenditions.length) return 'ready';
    if (this.publicationError || this.preparationNeedsRetry
      || ['failed', 'cancelled'].includes(this.renditionJob?.status || '')) return 'failed';
    return 'stale';
  }

  /** A preparation that is still running is re-checked rather than started again. */
  get preparationStillRunning(): boolean {
    return !!this.renditionJob && !this.preparedRenditions.length
      && !['failed', 'cancelled'].includes(this.renditionJob.status || '');
  }

  /** True when the drawer is reviewing an import for the map, not collecting a source. */
  get reviewingPublication(): boolean {
    return this.canReviewPublication && this.publishingService.canConfigure(this.projectId);
  }

  /** True when this source is already on the map, so the drawer saves edits instead of adding. */
  get isPublishedLayer(): boolean {
    return this.dxfVersion?.status === 'published';
  }

  /** Everything that decides what this drawer would publish. */
  /**
   * True when every edit since the baseline is one the published tiles do not
   * depend on: layer names, group, order, default visibility and opacity. The
   * appearance inputs set `reviewDirty` and always need a rebuild.
   */
  private get tilesUnchanged(): boolean {
    if (this.reviewDirty || !this.reviewBaseline) return false;
    try {
      const strip = (fingerprint: string) => {
        const { layers, settings } = JSON.parse(fingerprint);
        const { defaultVisible, defaultOpacity, ...style } = settings;
        return JSON.stringify({ layers: layers.map(([key, , selected]: [string, string, boolean]) => [key, selected]), style });
      };
      return strip(this.reviewBaseline) === strip(this.publishFingerprint());
    } catch { return false; }
  }

  /** The renditions this sublayer is published with today, for a save that keeps them. */
  private publishedRenditionsFor(sublayerKey: string): { renditionIds: string[]; minZoom: number; maxZoom: number } | null {
    const layer = this.publicationCatalog?.layers.find(item => item.sourceVersionId === this.dxfVersion?.id && item.sublayerKey === sublayerKey);
    if (!layer?.assets.length) return null;
    return { renditionIds: layer.assets.map(asset => asset.id), minZoom: layer.minZoom, maxZoom: layer.maxZoom };
  }

  /**
   * A published layer whose tiles are unchanged saves with the renditions it
   * already has instead of building a preview first (a rename or a group move
   * should not wait on the renderer).
   */
  get reusesPublishedRenditions(): boolean {
    if (!this.isPublishedLayer || this.preparedRenditions.length || !this.tilesUnchanged) return false;
    const selected = this.reviewLayers.filter(row => row.selected);
    return selected.length > 0 && selected.every(row => !!this.publishedRenditionsFor(row.key));
  }

  private publishFingerprint(): string {
    return JSON.stringify({
      layers: this.reviewLayers.map(row => [row.key, row.name.trim(), row.selected]),
      settings: this.reviewSettings,
      groupId: this.groupId,
      newGroupName: this.newGroupName.trim(),
      layerOrder: this.layerOrder,
    });
  }

  /**
   * True once anything that would change what is published has been edited.
   * `reviewDirty` covers the appearance inputs, which announce themselves through
   * publicationConfigurationChanged(); the fingerprint covers the rest (group,
   * order, default opacity and visibility, sublayer names and selection).
   */
  get drawerDirty(): boolean {
    return this.reviewDirty || (!!this.reviewBaseline && this.publishFingerprint() !== this.reviewBaseline);
  }

  /**
   * A review that asks for no confirmation checkbox: a clean first import, or a
   * layer already on the map, whose edits are recoverable through the revision
   * history. A first review carrying warnings still has to be confirmed.
   */
  get skipsReviewConfirmation(): boolean {
    return this.oneStepAdd || this.isPublishedLayer;
  }

  /** The drawer's one primary action; it builds the preview first when that is needed. */
  get canAddToMap(): boolean {
    if (!this.publicationLoaded || this.publicationBusy || this.publicationConflict) return false;
    // A layer already on the map stays put until something about it changes.
    if (this.isPublishedLayer && !this.drawerDirty) return false;
    if (!this.preparedRenditions.length) return true;
    return this.skipsReviewConfirmation || this.reviewed;
  }

  get addToMapLabel(): string {
    if (this.publishing) return this.isPublishedLayer ? 'Saving…' : 'Adding to map…';
    if (this.preparing) return 'Building preview…';
    return this.isPublishedLayer ? 'Save changes' : 'Add to map';
  }

  private currentPublication(): boolean {
    const current = !this.publicationAbort.signal.aborted && this.publicationVersion === this.dxfVersion?.id && this.publicationProject === this.projectId
      && this.publicationAccount === this.authService.getCurrentUser()?.id;
    if (!current && this.publicationVersion) {
      this.stopWork(); this.dxfVersion = null; this.name = ''; this.sourceFilename = null;
      this.closePanel();
    }
    return current;
  }

  private requirePublication(): void {
    if (!this.currentPublication()) throw new DOMException('Project or account changed', 'AbortError');
  }

  async loadPublicationReview(): Promise<void> {
    if (!this.canReviewPublication || !this.publishingService.canConfigure(this.projectId)) return;
    this.publicationVersion = this.dxfVersion.id;
    this.publicationAccount = this.authService.getCurrentUser()?.id;
    this.publicationProject = this.projectId;
    const generation = this.workGeneration;
    this.publicationBusy = true;
    this.publicationError = '';
    try {
      const catalog = await this.publishingService.catalog(this.projectId, this.publicationAbort.signal);
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      this.publicationCatalog = catalog;
      const sourceLayers = catalog?.layers.filter(layer => layer.sourceVersionId === this.dxfVersion.id
        || this.dxfVersion.sourceLayerIds?.includes(layer.id)) || [];
      // A single-sublayer import takes the name the user gave the source; multi-sublayer imports keep each sublayer's own name.
      const singleName = this.dxfVersion.sublayers.length === 1 ? this.name.trim() : '';
      // A new placement is named for the revision it shows ("C-112 Rev C", from the converter), so a re-placement
      // renames the layer it takes over instead of keeping the old revision's name. Once this version is on the map,
      // reopening it keeps whatever the publisher renamed it to.
      const placed = this.dxfVersion.format === 'placed_plan' && !catalog?.layers.some(layer => layer.sourceVersionId === this.dxfVersion.id);
      this.reviewLayers = this.dxfVersion.sublayers.map(layer => ({ key: layer.key,
        name: (placed && layer.name) || sourceLayers.find(item => item.sublayerKey === layer.key)?.name || singleName || layer.name || layer.key,
        selected: (!this.data?.layerId || catalog?.layers.some(item => item.id === this.data.layerId && item.sublayerKey === layer.key)) && this.dxfVersion.assets.some(asset => asset.sublayerKey === layer.key && ['geojson', 'raster'].includes(asset.kind) && asset.role === 'geometry') }));
      // One sublayer → the top Name field is the map layer's name (see nameEditsMapLayer).
      if (this.reviewLayers.length === 1) this.name = this.reviewLayers[0].name;
      const currentLayer = catalog?.layers.find(layer => layer.id === this.data?.layerId)
        || sourceLayers[0];
      const requestedGroupId = catalog?.groups.find(group => group.id === this.data?.groupId)?.id;
      if (requestedGroupId) this.groupId = requestedGroupId;
      else if (currentLayer) this.groupId = catalog?.groups.some(group => group.id === currentLayer.groupId) ? currentLayer.groupId : null;
      else if (this.data?.groupId && this.data.groupName?.trim()) {
        // Not published yet: create it under the id the API already holds, so the catalog and the API share one group.
        this.groupId = '';
        this.newGroupName = this.data.groupName.trim().slice(0, 256);
      } else if (this.reviewLayers.filter(layer => layer.selected).length > 1 && this.sourceGroupName) {
        // Multi-sublayer imports (DXF, KMZ) default to a group named after the source so crews can toggle them together.
        const name = this.sourceGroupName;
        const existing = catalog?.groups.find(group => group.name.toLowerCase() === name.toLowerCase());
        this.groupId = existing?.id || '';
        if (!existing) this.newGroupName = name;
      } else this.groupId = null; // Single-sublayer imports stay ungrouped by default.
      const defaultsScope = JSON.stringify([this.publicationProject, this.publicationAccount, this.publicationVersion]);
      if (this.reviewDefaultsScope !== defaultsScope) {
        const legacy = this.publishingService.legacyDefaults(this.dxfVersion);
        const published = currentLayer || (legacy && this.publicationCatalog?.layers.find(layer =>
          layer.id === this.dxfVersion.legacyReferenceLayerId && layer.sourceVersionId === this.dxfVersion.id
          && this.reviewLayers.some(row => row.selected && row.key === layer.sublayerKey)));
        this.layerOrder = published?.order ?? legacy?.sortOrder ?? Math.max(0, ...(this.publicationCatalog?.layers.map(layer => layer.order + 1) || []));
        this.legacyStyleWarning = legacy?.warning || '';
        if (legacy || published) {
          this.reviewSettings = { ...this.reviewSettings, defaultVisible: published?.defaultVisible ?? false,
            defaultOpacity: published?.defaultOpacity ?? legacy?.defaultOpacity ?? 1,
            ...this.settingsFromStyle(published?.style) };
          this.reviewSeeded = true;
        } else {
          // A new layer starts off: crews turn on what they need in the layers
          // panel, so adding design layers never buries the map by default.
          this.reviewSettings.defaultVisible = false;
          if (!this.reviewSeeded && !this.reviewDirty) {
            // Not on the map yet: pick up the draft the reviewer last prepared, so
            // closing and reopening from Pending does not throw their choices away.
            await this.seedReviewFromDraft(generation);
            // Nothing saved to go back to, so start from the file's own colours
            // and its first few readable fields for the feature card.
            this.applySourceColorDefaults();
            this.applyPopupFieldDefaults();
          }
        }
        // Seed trusted migration defaults once. Refreshes and conflict retries
        // must retain the publisher's explicit visibility/opacity/order edits.
        this.reviewDefaultsScope = defaultsScope;
      }
      this.newGroupId = this.groupId === '' && this.data?.groupId && !catalog?.groups.some(group => group.id === this.data.groupId) ? this.data.groupId
        : `design-${Array.from(crypto.getRandomValues(new Uint8Array(8)), value => value.toString(16).padStart(2, '0')).join('')}`;
      this.reviewBaseline = this.publishFingerprint();
      this.publicationLoaded = true;
      void this.showSourcePreview();
    } catch (error) { if (generation === this.workGeneration) this.publicationFailure(error); }
    finally { if (generation === this.workGeneration) this.publicationBusy = false; }
    // One-step add: a clean import prepares itself, and preparePublication() opens
    // the preview, so the reviewer lands on a finished review with nothing to click.
    if (generation === this.workGeneration && this.publicationLoaded && this.oneStepAdd) await this.preparePublication();
  }

  /** Appearance and popup fields read from a published or prepared style. */
  private settingsFromStyle(style: ProjectMapStyle | undefined | null): Partial<MapReviewSettings> {
    const symbols = style?.symbols || [];
    const point = symbols.find(symbol => symbol.geometry === 'point');
    const line = symbols.find(symbol => symbol.geometry === 'line');
    const area = symbols.find(symbol => symbol.geometry === 'polygon');
    return {
      color: symbols[0]?.color ?? this.reviewSettings.color,
      pointSize: point?.size ?? this.reviewSettings.pointSize,
      strokeWidth: line?.strokeWidth ?? area?.strokeWidth ?? this.reviewSettings.strokeWidth,
      fillAlpha: area?.fillAlpha ?? this.reviewSettings.fillAlpha,
      colorBy: this.publishingService.publishedColorBy(style),
      popupFields: style?.popupFields?.map(field => field.field).join(', ') ?? this.reviewSettings.popupFields,
    };
  }

  /**
   * Seed the settings from the newest prepared rendition of this version. A
   * "Rebuild map preview" stores the style it was built with, which is the only
   * place a draft survives once the drawer closes.
   */
  private async seedReviewFromDraft(generation: number): Promise<void> {
    if (!this.dxfVersion?.id) return;
    let rows: MapPreparedRendition[] = [];
    try { rows = await this.publishingService.preparedRows(this.projectId, this.dxfVersion.id, this.publicationAbort.signal); }
    catch { return; }
    if (generation !== this.workGeneration || this.reviewSeeded || this.reviewDirty) return;
    const latest = rows.length ? rows[rows.length - 1] : undefined;
    if (!latest?.style) return;
    this.reviewSettings = { ...this.reviewSettings, ...this.settingsFromStyle(latest.style) };
    this.reviewSeeded = true;
    this.applySourcePreviewStyle();
  }

  /**
   * Start an empty popup from the first few fields the import listed, so a new
   * layer's feature cards are never blank. The inventory has them even when the
   * file is too large for the location preview to read.
   */
  private applyPopupFieldDefaults(): void {
    if (this.reviewSeeded || this.reviewDirty || this.popupFieldList.length) return;
    const fields: string[] = [];
    for (const row of this.reviewLayers.filter(layer => layer.selected)) {
      for (const name of this.dxfVersion?.sublayers.find(layer => layer.key === row.key)?.fieldNames || []) {
        if (typeof name === 'string' && name.trim() && !fields.includes(name)) fields.push(name);
      }
    }
    if (fields.length) this.reviewSettings.popupFields = fields.slice(0, DEFAULT_POPUP_FIELD_COUNT).join(', ');
  }

  /** How many colour rows show before the "+N more" link. */
  readonly colorByCollapsedCount = 10;
  colorByExpanded = false;

  /**
   * The colour-by values grouped by legend label: a CAD layer drawn in several
   * pens (one irrigation zone layer, say) is one row with a picker per colour,
   * instead of the same name repeated down the list.
   */
  get colorByRows(): { label: string; entries: MapReviewColorBy['values'] }[] {
    const rows = new Map<string, MapReviewColorBy['values']>();
    for (const entry of this.reviewSettings.colorBy?.values || []) {
      const label = String(entry.label || entry.value);
      if (!rows.has(label)) rows.set(label, []);
      rows.get(label).push(entry);
    }
    return [...rows].map(([label, entries]) => ({ label, entries }));
  }

  get visibleColorByRows(): { label: string; entries: MapReviewColorBy['values'] }[] {
    const rows = this.colorByRows;
    return this.colorByExpanded ? rows : rows.slice(0, this.colorByCollapsedCount);
  }

  /** Popup field names as typed, trimmed and de-duplicated. */
  get popupFieldList(): string[] {
    return [...new Set(this.reviewSettings.popupFields.split(',').map(field => field.trim()).filter(Boolean))];
  }

  /** Typed popup fields the source does not carry; empty until a preview has read the source's fields. */
  get unknownPopupFields(): string[] {
    if (!this.sourceFieldNames.length) return [];
    return this.popupFieldList.filter(field => !this.sourceFieldNames.includes(field));
  }

  popupFieldChosen(name: string): boolean {
    return this.popupFieldList.includes(name);
  }

  addPopupField(name: string): void {
    if (this.popupFieldChosen(name)) return;
    this.reviewSettings.popupFields = [...this.popupFieldList, name].join(', ');
    this.publicationConfigurationChanged();
  }

  /** Snap typed names to the source's own spelling where only the case differs. */
  normalizePopupFields(): void {
    if (!this.sourceFieldNames.length) return;
    const byLower = new Map(this.sourceFieldNames.map(name => [name.toLowerCase(), name]));
    const fixed = this.popupFieldList.map(field => byLower.get(field.toLowerCase()) ?? field);
    const next = fixed.join(', ');
    if (next !== this.reviewSettings.popupFields) { this.reviewSettings.popupFields = next; this.publicationConfigurationChanged(); }
  }

  private trackFieldNames(properties: unknown, names: Set<string>): void {
    if (!properties || typeof properties !== 'object') return;
    for (const field of Object.keys(properties as Record<string, unknown>)) {
      // The converter's own style properties describe how a feature was drawn,
      // not what it is, so they are never offered as popup fields.
      if (!SOURCE_STYLE_FIELDS.includes(field) && field.length <= 256) names.add(field);
    }
  }

  /** Pending automatic preview rebuild after a settings change. */
  private previewUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  /** Rebuild the prepared preview shortly after the last settings change, so the
   *  reviewer never has to ask for it; a run of quick tweaks costs one rebuild. */
  /**
   * Edits mark the prepared preview out of date; nothing builds on its own.
   * A build is 20-40 s of worker time, so it runs when the reviewer asks
   * (Update preview in the footer) or when they add to / save the map. The
   * location preview already follows appearance changes instantly.
   */
  private schedulePreviewUpdate(): void {
    this.cancelPreviewUpdate();
  }

  private cancelPreviewUpdate(): void {
    if (this.previewUpdateTimer) clearTimeout(this.previewUpdateTimer);
    this.previewUpdateTimer = null;

  }

  publicationConfigurationChanged(): void {
    this.reviewDirty = true;
    // A reviewer who has picked a colour or another field has answered the
    // hint about this file carrying more colours than one layer can keep.
    this.sourceColorHint = '';
    // The location preview draws the chosen appearance, so it follows the change
    // straight away; only the prepared tiles have to be rebuilt.
    this.applySourcePreviewStyle();
    this.destroyPublicationPreview();
    this.preparedRenditions = [];
    this.preparedSelections = [];
    this.publicationRevision = undefined;
    this.renditionJob = undefined;
    this.publicationPhase = '';
    this.publicationError = '';
    this.schedulePreviewUpdate();
  }

  async preparePublication(retry = false): Promise<void> {
    if (this.publicationBusy || !this.currentPublication()) return;
    this.preparing = true;
    this.publicationBusy = true;
    this.publicationError = '';
    this.destroyPublicationPreview();
    const generation = this.workGeneration;
    try {
      const selections = this.publishingService.selections(this.dxfVersion, this.reviewLayers, this.reviewSettings);
      this.publishingService.estimate(this.dxfVersion, selections);
      if (retry || this.preparationNeedsRetry) this.preparationRetry++;
      this.preparationNeedsRetry = false;
      this.preparedSelections = selections;
      this.preparedRenditions = [];
      this.publicationPhase = 'Preparing reviewed map output…';
      const result = await this.publishingService.prepare(this.projectId, this.dxfVersion.id, selections,
        this.publishingService.requestKey(this.dxfVersion.id, selections, this.preparationRetry), this.publicationAbort.signal);
      // Settings changed while the request was out: this build is for nobody.
      if (generation !== this.workGeneration || this.preparedSelections !== selections) return;
      this.requirePublication();
      this.renditionJob = result.job;
      await this.pollPreparation(generation);
    } catch (error) { if (generation === this.workGeneration) this.publicationFailure(error); }
    finally { if (generation === this.workGeneration) { this.publicationBusy = false; this.preparing = false; } }
    await this.openPreparedPreview(generation);
  }

  async refreshPreparation(): Promise<void> {
    if (!this.renditionJob || this.publicationBusy || !this.currentPublication()) return;
    this.preparing = true;
    this.publicationBusy = true;
    this.publicationError = '';
    const generation = this.workGeneration;
    try { await this.pollPreparation(generation); }
    catch (error) { if (generation === this.workGeneration) this.publicationFailure(error); }
    finally { if (generation === this.workGeneration) { this.publicationBusy = false; this.preparing = false; } }
    await this.openPreparedPreview(generation);
  }

  /**
   * Show the prepared map as soon as a build lands, so the review needs no
   * separate "show preview" step. Runs after the preparing work released
   * publicationBusy, because previewPublication() waits on that flag too.
   */
  private async openPreparedPreview(generation: number): Promise<void> {
    if (generation !== this.workGeneration || !this.preparedRenditions.length || this.publicationBusy) return;
    await this.previewPublication();
  }

  private async pollPreparation(generation: number): Promise<void> {
    const deadline = Date.now() + 15 * 60 * 1000;
    while (Date.now() < deadline) {
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      // A settings change while this runs abandons the job it was watching: the
      // output would be built from settings nobody is looking at any more, and
      // a fresh preparation is already scheduled, so this one just stops.
      const jobId = this.renditionJob?.id;
      if (!jobId) return;
      const job = await this.publishingService.job(this.projectId, jobId, this.publicationAbort.signal);
      if (generation !== this.workGeneration || this.renditionJob?.id !== jobId) return;
      this.requirePublication();
      this.renditionJob = job;
      this.publicationPhase = job.status === 'queued' ? 'Waiting to prepare map output…' : 'Preparing tiles and feature inspection…';
      if (job.status === 'failed' || job.status === 'cancelled') {
        throw new Error(`Preparation ${job.status}. Reduce the zoom range or select fewer layers, then try again.${job.failureCode ? ` (${job.failureCode})` : ''}`);
      }
      if (job.status === 'succeeded') {
        const prepared = await this.publishingService.prepared(this.projectId, this.dxfVersion.id, this.preparedSelections, job, this.publicationAbort.signal);
        if (generation !== this.workGeneration) return;
        this.requirePublication();
        this.preparedRenditions = prepared;
        // The prepared map opens itself from here, so the phase says what is
        // happening rather than asking for a preview that needs no click.
        this.publicationPhase = 'Opening the map preview…';
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const signal = this.publicationAbort.signal;
        const aborted = () => { clearTimeout(timerId); reject(signal.reason); };
        const timerId = setTimeout(() => { signal.removeEventListener('abort', aborted); resolve(); }, 2500);
        signal.addEventListener('abort', aborted, { once: true });
      });
    }
    throw new Error('Preparation is still running. Refresh its status to continue.');
  }

  /**
   * A click on the prepared preview looks up the features under it with the
   * same lookup the published map uses, so the card shows exactly the popup
   * fields chosen in this drawer, before anything is published.
   */
  async inspectPreview(view: __esri.MapView, event: __esri.ViewClickEvent): Promise<void> {
    // Only the expanded preview inspects: the inline map is too small to read a panel over.
    if (!this.previewExpanded || !this.projectId || !this.currentPublication()) return;
    const tolerance = 8;
    const corners = [view.toMap({ x: event.x - tolerance, y: event.y + tolerance }), view.toMap({ x: event.x + tolerance, y: event.y - tolerance })]
      .map(point => point?.spatialReference?.isWebMercator ? webMercatorUtils.webMercatorToGeographic(point) as __esri.Point : point);
    if (corners.some(point => !point)) return;
    const bbox = [Math.min(corners[0].x, corners[1].x), Math.min(corners[0].y, corners[1].y),
      Math.max(corners[0].x, corners[1].x), Math.max(corners[0].y, corners[1].y)];
    const renditions = this.preparedRenditions.filter(item => item.kind === 'raster_xyz' && item.role === 'geometry');
    if (!renditions.length) return;
    this.previewFeaturesBusy = true; this.previewFeaturesNote = ''; this.previewFeatures = [];
    const generation = this.previewGeneration;
    try {
      const pages = await Promise.all(renditions.map(item => this.manifestService.renditionFeatures(this.projectId, item.id, bbox, 5)));
      if (generation !== this.previewGeneration) return;
      const cards: PreviewFeatureCard[] = [];
      pages.forEach((page, index) => {
        const rendition = renditions[index];
        const sublayer = this.reviewLayers.find(row => row.key === rendition.sublayerKey)?.name || rendition.sublayerKey;
        const popupFields = rendition.style?.popupFields || [];
        for (const feature of page.features) {
          const fields = popupFields
            .filter(field => feature.properties[field.field] !== undefined && feature.properties[field.field] !== null && feature.properties[field.field] !== '')
            .map(field => ({ label: field.label || field.field, value: String(feature.properties[field.field]) }));
          cards.push({ key: `${rendition.id}:${feature.id}`, sublayer, label: fields[0]?.value || `Feature ${cards.length + 1}`, fields, geometry: feature.geometry,
            properties: feature.properties, fieldNames: popupFields.map(field => field.field) });
        }
      });
      const [{ default: Point }] = await Promise.all([import('@arcgis/core/geometry/Point')]);
      const perPixel = metresPerPixel(view, event);
      for (const card of cards) {
        const arcgis = geoJsonToArcGis(card.geometry);
        card.distance = await screenDistance(view, arcgis ? (arcgis.type === 'point' ? new Point(arcgis as __esri.PointProperties) : null) : null, event);
        if (arcgis && arcgis.type !== 'point') {
          const [{ default: Polyline }, { default: Polygon }] = await Promise.all([import('@arcgis/core/geometry/Polyline'), import('@arcgis/core/geometry/Polygon')]);
          card.distance = await screenDistance(view, arcgis.type === 'polyline' ? new Polyline(arcgis as __esri.PolylineProperties)
            : arcgis.type === 'polygon' ? new Polygon(arcgis as __esri.PolygonProperties) : null, event);
        }
        card.metres = Number.isFinite(card.distance) ? card.distance * perPixel : undefined;
      }
      if (generation !== this.previewGeneration) return;
      this.showPreviewFeatures(cards);
    } catch (error) {
      if (generation !== this.previewGeneration) return;
      this.previewFeatures = null;
      this.previewFeaturesNote = error?.error?.message || error?.message || 'Could not look up features here.';
    } finally { if (generation === this.previewGeneration) this.previewFeaturesBusy = false; }
  }

  /** Geometry renditions in the preview: with more than one, each card names its sublayer. */
  get renditionCount(): number { return this.preparedRenditions.filter(item => item.kind === 'raster_xyz' && item.role === 'geometry').length; }

  /** Both previews share one card; the source preview hit-tests its own features, the prepared one asks the API. */
  private attachSourcePreviewClick(view: __esri.MapView): void {
    this.sourcePreviewClickHandle?.remove();
    this.sourcePreviewClickHandle = view.on('click', event => { void this.inspectSourcePreview(view, event); });
  }

  async inspectSourcePreview(view: __esri.MapView, event: __esri.ViewClickEvent): Promise<void> {
    if (!this.sourcePreviewExpanded) return;
    const generation = this.workGeneration;
    this.previewFeaturesBusy = true; this.previewFeaturesNote = ''; this.previewFeatures = [];
    try {
      const hit = await view.hitTest(event);
      if (generation !== this.workGeneration || view.destroyed) return;
      const seen = new Set<string>();
      const cards: PreviewFeatureCard[] = [];
      for (const result of hit.results) {
        const graphic = (result as __esri.GraphicHit).graphic;
        // Only our own features: the basemap's labels and the footprint outline are hit results too.
        if (result.type !== 'graphic' || !graphic?.attributes || !['geojson', 'feature'].includes(graphic.layer?.type)
          || !Object.keys(graphic.attributes).length) continue;
        const index = graphic.attributes.sn_index;
        const stored = Number.isInteger(index) ? this.sourcePreviewProperties.get(index) : null;
        const key = stored ? `index:${index}` : JSON.stringify(graphic.attributes);
        if (seen.has(key)) continue;
        seen.add(key);
        const properties = stored ? stored.properties : graphic.attributes as Record<string, unknown>;
        const fields = this.popupFieldList
          .filter(name => properties[name] !== undefined && properties[name] !== null && properties[name] !== '')
          .map(name => ({ label: name, value: String(properties[name]) }));
        const distance = await screenDistance(view, graphic.geometry, event);
        cards.push({ key, sublayer: stored?.sublayer || '', label: fields[0]?.value || `Feature ${cards.length + 1}`, fields, graphic,
          properties, fieldNames: this.popupFieldList, distance, metres: Number.isFinite(distance) ? distance * metresPerPixel(view, event) : undefined });
      }
      if (generation !== this.workGeneration) return;
      this.showPreviewFeatures(cards);
      if (cards.length && !this.popupFieldList.length) this.previewFeaturesNote = 'Choose popup fields to see them here.';
    } catch (error) {
      if (generation !== this.workGeneration) return;
      this.previewFeatures = null;
      this.previewFeaturesNote = error?.message || 'Could not read the feature here.';
    } finally { if (generation === this.workGeneration) this.previewFeaturesBusy = false; }
  }

  /** Same shape as the published map's panel: choose a feature, see its fields, hover to find it on the map. */
  private showPreviewFeatures(cards: PreviewFeatureCard[]): void {
    const nearestFirst = [...cards].sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    this.previewFeaturesHidden = Math.max(0, nearestFirst.length - MAX_PREVIEW_CHOICES);
    this.previewFeatures = nearestFirst.slice(0, MAX_PREVIEW_CHOICES);
    titleLookAlikes(this.previewFeatures, card => card.sublayer);
    for (const card of this.previewFeatures) {
      const distance = distanceText(card.metres);
      const sublayer = this.previewShowsSublayers ? card.sublayer : '';
      card.meta = [sublayer, distance].filter(Boolean).join(' · ');
    }
    // One hit opens its details straight away; several start as a list.
    this.selectedPreviewFeature = this.previewFeatures.length === 1 ? this.previewFeatures[0] : null;
    this.previewFeatureView = this.selectedPreviewFeature ? 'details' : 'list';
    if (!cards.length) this.previewFeaturesNote = 'No features here.';
    else if (cards.every(card => !card.fields.length)) this.previewFeaturesNote = 'These features have none of the chosen popup fields.';
    void this.highlightPreviewFeature(this.selectedPreviewFeature);
  }

  selectPreviewFeature(card: PreviewFeatureCard): void {
    this.selectedPreviewFeature = card;
    this.previewFeatureView = 'details';
    void this.highlightPreviewFeature(card);
  }

  backToPreviewFeatures(): void {
    this.selectedPreviewFeature = null;
    this.previewFeatureView = 'list';
    void this.highlightPreviewFeature(null);
  }

  hoverPreviewFeature(card: PreviewFeatureCard | null): void {
    void this.highlightPreviewFeature(card || this.selectedPreviewFeature);
  }

  private async highlightPreviewFeature(card: PreviewFeatureCard | null): Promise<void> {
    // Hover and select overlap; only the latest request may leave a highlight behind.
    const generation = ++this.previewHighlightGeneration;
    this.previewHighlightHandle?.remove(); this.previewHighlightHandle = undefined;
    this.previewHighlightLayer?.removeAll();
    if (!card) return;
    if (card.graphic?.layer && this.sourcePreviewView && !this.sourcePreviewView.destroyed) {
      // Source preview: the SDK's own highlight on the drawn feature.
      const layerView = await this.sourcePreviewView.whenLayerView(card.graphic.layer as __esri.Layer).catch(() => null);
      if (generation !== this.previewHighlightGeneration) return;
      if (layerView && 'highlight' in layerView) {
        this.previewHighlightHandle = (layerView as __esri.FeatureLayerView).highlight(card.graphic);
      }
      return;
    }
    if (card.geometry && this.previewView && !this.previewView.destroyed) {
      // Prepared preview draws tiles, so outline the feature from the geometry the lookup returned.
      const [{ default: Graphic }, { default: GraphicsLayer }] = await Promise.all([import('@arcgis/core/Graphic'), import('@arcgis/core/layers/GraphicsLayer')]);
      if (generation !== this.previewHighlightGeneration || !this.previewView || this.previewView.destroyed) return;
      if (!this.previewHighlightLayer) {
        this.previewHighlightLayer = new GraphicsLayer({ listMode: 'hide' });
        this.previewView.map.add(this.previewHighlightLayer);
      }
      const geometry = geoJsonToArcGis(card.geometry);
      if (!geometry) return;
      const symbol = featureHighlightSymbol(geometry.type);
      this.previewHighlightLayer.add(new Graphic({ geometry: geometry as unknown as __esri.GeometryUnion, symbol: symbol as unknown as __esri.SymbolUnion }));
    }
  }

  /** With more than one source sublayer, each card names the sublayer its feature belongs to. */
  get previewShowsSublayers(): boolean { return (this.dxfVersion?.sublayers?.length || 0) > 1; }

  closePreviewFeatures(): void {
    this.previewFeatures = null; this.selectedPreviewFeature = null; this.previewFeatureView = 'list'; this.previewFeaturesNote = ''; this.previewFeaturesHidden = 0;
    void this.highlightPreviewFeature(null);
  }

  private destroyPublicationPreview(): void {
    this.previewClickHandle?.remove(); this.previewClickHandle = undefined;
    this.closePreviewFeatures();
    this.previewHighlightLayer = undefined;
    this.previewGeneration++;
    this.previewSatellite = false;
    this.previewAbort?.abort();
    this.previewAbort = undefined;
    this.exitPreviewFullscreen(false);
    this.previewExpanded = false;
    this.closePreviewFeatures();
    this.previewRenderedCount = 0;
    if (this.previewExpires) clearTimeout(this.previewExpires);
    this.previewExpires = undefined;
    this.previewView?.destroy();
    this.previewView = undefined;
    this.previewReady = false;
    this.previewOpen = false;
    this.reviewed = false;
  }

  async previewPublication(): Promise<void> {
    if (this.publicationBusy || !this.preparedRenditions.length || !this.currentPublication()) return;
    try { this.preparedRenditions.forEach(asset => this.publishingService.previewBounds(asset.bounds)); }
    catch (error) { this.publicationFailure(error); return; }
    this.destroyPublicationPreview();
    this.previewAbort = new AbortController();
    const signal = this.previewAbort.signal;
    const previewGeneration = this.previewGeneration;
    const active = () => previewGeneration === this.previewGeneration && !signal.aborted && this.currentPublication();
    this.previewOpen = true;
    this.previewExpired = false;
    this.publicationBusy = true;
    this.publicationError = '';
    const generation = this.workGeneration;
    let view: __esri.MapView;
    let previewMap: __esri.Map;
    try {
      const [{ default: ArcMap }, { default: MapView }, { default: BaseTileLayer }, { default: TileInfo }, reactiveUtils] = await Promise.all([
        import('@arcgis/core/Map'), import('@arcgis/core/views/MapView'), import('@arcgis/core/layers/BaseTileLayer'), import('@arcgis/core/layers/support/TileInfo'), import('@arcgis/core/core/reactiveUtils'),
      ]);
      if (!active()) return;
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (!active()) return;
      if (!this.previewElement?.nativeElement) throw new Error('The preview could not be opened. Try again.');
      const map = new ArcMap({ basemap: 'gray-vector' });
      previewMap = map;
      const raster = this.preparedRenditions.filter(asset => asset.kind === 'raster_xyz' && asset.role === 'geometry');
      // Prepared XYZ levels use the 256-pixel Web Mercator grid. The basemap's
      // default 512-pixel grid shifts the same zoom number by one level.
      const tileInfo = TileInfo.create({ size: 256, numLODs: 25 });
      const rendered = new Set<string>();
      let failed = false;
      let settled = false;
      let expiresIn = 600;
      for (const asset of raster) {
        const grant = await this.publishingService.previewGrant(this.projectId, asset.id, signal);
        if (!active()) { map.destroy(); return; }
        if (!grant.grant) throw new Error('A private preview grant is unavailable.');
        expiresIn = Math.min(expiresIn, grant.expiresIn);
        const urlTemplate = this.publishingService.tileUrl(grant.grant);
        const layer = new BaseTileLayer({ id: `review-${asset.id}`, title: this.reviewLayers.find(row => row.key === asset.sublayerKey)?.name,
          tileInfo, opacity: this.previewOpacity, minScale: tileInfo.lods[asset.minZoom].scale,
          maxScale: tileInfo.lods[asset.maxZoom].scale });
        layer.fetchTile = async (level, row, col, options = {}) => {
          const tileAbort = new AbortController();
          const abort = () => tileAbort.abort();
          signal.addEventListener('abort', abort, { once: true });
          options.signal?.addEventListener('abort', abort, { once: true });
          try {
            if (!active() || options.signal?.aborted) throw new DOMException('Preview closed', 'AbortError');
            const bytes = await this.publishingService.readPreview(urlTemplate.replace('{level}', String(level)).replace('{row}', String(row)).replace('{col}', String(col)), tileAbort.signal);
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = 256;
            if (bytes) {
              const bitmap = await createImageBitmap(new Blob([bytes as BlobPart], { type: 'image/png' }));
              try { canvas.getContext('2d').drawImage(bitmap, 0, 0, 256, 256); } finally { bitmap.close(); }
              if (active()) {
                rendered.add(asset.id); this.previewRenderedCount = rendered.size;
                if (settled && !failed && rendered.size === raster.length) this.previewReady = true;
              }
            }
            return canvas;
          } catch (error) {
            if (active() && !tileAbort.signal.aborted) {
              failed = true; this.previewReady = false; this.reviewed = false; this.publicationFailure(error);
            }
            throw error;
          } finally {
            signal.removeEventListener('abort', abort); options.signal?.removeEventListener('abort', abort);
          }
        };
        map.add(layer);
      }
      view = new MapView({ container: this.previewElement.nativeElement, map,
        // The server chose the range, so the preview reads it off the tiles.
        constraints: { lods: tileInfo.lods, ...this.preparedZoomRange() },
        ui: { components: ['zoom', 'attribution'] } });
      this.previewView = view;
      await view.when();
      this.previewClickHandle?.remove();
      this.previewClickHandle = view.on('click', event => { void this.inspectPreview(view, event); });
      if (!active()) { view.destroy(); return; }
      await this.zoomPreview(false);
      await reactiveUtils.whenOnce(() => !view.updating, { signal });
      if (!active()) { view.destroy(); return; }
      settled = true;
      this.previewReady = !failed && rendered.size === raster.length;
      this.previewExpires = setTimeout(() => { this.destroyPublicationPreview(); this.previewExpired = true; }, Math.max(1000, (expiresIn - 30) * 1000));
    } catch (error) {
      view?.destroy(); previewMap?.destroy();
      if (generation === this.workGeneration && previewGeneration === this.previewGeneration) { this.destroyPublicationPreview(); this.publicationFailure(error); }
    } finally { if (generation === this.workGeneration) this.publicationBusy = false; }
  }

  private destroySourcePreview(): void {
    this.sourcePreviewClickHandle?.remove(); this.sourcePreviewClickHandle = undefined;
    this.sourcePreviewProperties = new Map();
    this.closePreviewFeatures();
    this.sourcePreviewStyleLayers = [];
    this.sourcePreviewSatellite = false;
    this.livePreviewAbort?.abort();
    this.livePreviewAbort = undefined;
    this.livePreviewExtent = undefined;
    this.exitPreviewFullscreen(true);
    this.sourcePreviewView?.destroy(); this.sourcePreviewView = undefined;
    this.sourcePreviewUrls.forEach(url => URL.revokeObjectURL(url)); this.sourcePreviewUrls = [];
    if (this.sourcePreviewExpires) clearTimeout(this.sourcePreviewExpires);
    this.sourcePreviewExpires = undefined; this.sourcePreviewOpen = false; this.sourcePreviewExpanded = false;
    this.sourcePreviewSheetCount = 0;
  }

  private clearArcGISInspection(): void {
    this.arcgisGeneration++;
    this.arcgisRequest?.unsubscribe();
    this.arcgisInspectBusy = false;
    this.arcgisInspection = null;
    this.arcgisLayers = [];
    this.arcgisError = '';
    this.arcgisCaptureKey = '';
    this.arcgisCaptureFingerprint = '';
  }

  inspectArcGIS(): void {
    if (this.submitting || !this.sourceUrl.trim() || !this.publishingService.canConfigure(this.projectId)) return;
    this.clearArcGISInspection();
    const generation = this.arcgisGeneration;
    this.arcgisInspectBusy = true;
    this.arcgisRequest = this.sourceService.inspectArcGIS(this.projectId, this.sourceUrl.trim())
      .pipe(takeUntil(this.workCancelled), takeUntil(this.onDestroy)).subscribe({
        next: inspection => {
          if (generation !== this.arcgisGeneration) return;
          this.arcgisInspectBusy = false;
          this.arcgisInspection = inspection;
          this.arcgisLayers = inspection.sublayers.map(layer => {
            const supported = layer.supportsQuery && !!layer.objectIdField
              && ['esriGeometryPoint', 'esriGeometryMultipoint', 'esriGeometryPolyline', 'esriGeometryPolygon'].includes(layer.geometryType);
            return { id: layer.id, name: layer.name, supported,
              selected: supported && (!this.newVersionLayerIds || this.newVersionLayerIds.includes(layer.id)) };
          });
          if (!this.arcgisLayers.some(layer => layer.supported)) this.arcgisError = 'This service has no supported feature layers to capture.';
        },
        error: () => {
          if (generation !== this.arcgisGeneration) return;
          this.arcgisInspectBusy = false;
          this.arcgisError = 'The ArcGIS service could not be inspected. Use a public HTTPS FeatureServer or MapServer URL and try again.';
        },
      });
  }

  private captureArcGIS(): void {
    const source = { name: this.name.trim(), url: this.sourceUrl.trim(),
      ...(this.newVersionExtent ? { extent: this.newVersionExtent } : {}),
      layerIds: this.arcgisLayers.filter(layer => layer.selected && layer.supported).map(layer => layer.id).sort((a, b) => a - b) };
    if (!this.arcgisInspection || !source.layerIds.length || this.arcgisInspectBusy) return;
    const fingerprint = JSON.stringify(source);
    if (!this.arcgisCaptureKey || this.arcgisCaptureFingerprint !== fingerprint) {
      this.arcgisCaptureKey = Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('');
      this.arcgisCaptureFingerprint = fingerprint;
    }
    this.submitting = true;
    this.submitError = null;
    const generation = this.workGeneration;
    this.arcgisRequest = this.sourceService.captureArcGIS(this.projectId, source, this.arcgisCaptureKey, this.newVersionSourceId)
      .pipe(takeUntil(this.workCancelled), takeUntil(this.onDestroy)).subscribe({
        next: result => {
          if (generation !== this.workGeneration) return;
          this.submitting = false;
          this.acceptVersion(result.version);
          this.sourceService.listChanged.next({ sourceId: result.version.sourceId, focus: true });
          this.startPolling(result.version.id);
        },
        error: error => { if (generation === this.workGeneration) this.onSubmitError(error); },
      });
  }

  onLiveSourceUrlChange(): void {
    this.clearArcGISInspection();
    this.destroySourcePreview();
    this.sourcePreviewBusy = false;
    this.sourcePreviewError = '';
  }

  async showLivePreview(): Promise<void> {
    if (this.sourceType !== 'arcgis_url' || !this.publishingService.canConfigure(this.projectId)) return;
    this.destroySourcePreview();
    const controller = new AbortController();
    this.livePreviewAbort = controller;
    const { signal } = controller;
    const generation = this.workGeneration;
    const account = this.authService.getCurrentUser()?.id;
    const active = () => !signal.aborted && generation === this.workGeneration
      && account === this.authService.getCurrentUser()?.id && this.publishingService.canConfigure(this.projectId);
    this.sourcePreviewBusy = true;
    this.sourcePreviewError = '';
    let map: __esri.Map;
    const deadline = setTimeout(() => {
      if (!active()) return;
      this.destroySourcePreview();
      this.sourcePreviewBusy = false;
      this.sourcePreviewError = 'The ArcGIS service took too long to respond. Try refreshing the preview.';
    }, 30000);
    try {
      let url: URL;
      try { url = new URL(this.sourceUrl.trim()); }
      catch { throw new Error('Use the URL of a single FeatureServer layer, ending in its layer number.'); }
      if (!['https:', 'http:'].includes(url.protocol) || !/\/FeatureServer\/\d+\/?$/i.test(url.pathname)) {
        throw new Error('Use the URL of a single FeatureServer layer, ending in its layer number.');
      }
      this.sourcePreviewOpen = true;
      const [{ default: ArcMap }, { default: MapView }, { default: FeatureLayer }, { executeForExtent }] = await Promise.all([
        import('@arcgis/core/Map'), import('@arcgis/core/views/MapView'), import('@arcgis/core/layers/FeatureLayer'),
        import('@arcgis/core/rest/query'),
      ]);
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (!active()) return;
      if (!this.sourcePreviewElement?.nativeElement) throw new Error('The map preview could not be opened.');
      const layer = new FeatureLayer({ url: url.href, opacity: this.sourcePreviewOpacity });
      map = new ArcMap({ basemap: 'gray-vector', layers: [layer] });
      const view = new MapView({ container: this.sourcePreviewElement.nativeElement, map,
        ui: { components: ['zoom', 'attribution'] } });
      this.sourcePreviewView = view;
      this.attachSourcePreviewClick(view);
      await layer.load({ signal });
      if (!active()) return;
      // The SDK also requests a count by default. Some hosted services return stale
      // bounds for that combined request; extent-only queries calculate the feature bounds.
      const result: { extent: __esri.Extent; count?: number } = await executeForExtent(url.href,
        layer.createQuery(), { signal, query: { returnCountOnly: false } });
      if (!active()) return;
      if (!result.extent || result.count === 0) throw new Error('This ArcGIS layer has no features to preview.');
      this.livePreviewExtent = result.extent;
      await view.when();
      if (!active()) return;
      await this.zoomPreview(true);
      await view.whenLayerView(layer);
    } catch (error) {
      if (active()) {
        this.destroySourcePreview();
        this.sourcePreviewError = error?.message?.startsWith('Use the URL') || error?.message?.includes('no features to preview')
          ? error.message : 'The ArcGIS layer could not load. Check the URL and service access, then refresh the preview.';
      }
      map?.destroy();
    } finally {
      clearTimeout(deadline);
      if (this.livePreviewAbort === controller) this.sourcePreviewBusy = false;
      else if (generation === this.workGeneration && this.sourcePreviewError) this.sourcePreviewBusy = false;
    }
  }

  async showSourcePreview(): Promise<void> {
    if (this.sourcePreviewBusy || !this.currentPublication()) return;
    this.destroySourcePreview(); this.sourcePreviewOpen = true; this.sourcePreviewBusy = true; this.sourcePreviewError = '';
    const generation = this.workGeneration;
    let map: __esri.Map;
    try {
      if (!this.dxfVersion.sublayers.length) throw new Error('No source location is available to preview.');
      this.dxfVersion.sublayers.forEach(layer => this.publishingService.previewBounds(layer.bounds));
      const [{ default: ArcMap }, { default: MapView }, { default: GeoJSONLayer }, { default: GraphicsLayer }, { default: Graphic }] = await Promise.all([
        import('@arcgis/core/Map'), import('@arcgis/core/views/MapView'), import('@arcgis/core/layers/GeoJSONLayer'),
        import('@arcgis/core/layers/GraphicsLayer'), import('@arcgis/core/Graphic'),
      ]);
      if (generation !== this.workGeneration) return;
      this.requirePublication(); await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      if (!this.sourcePreviewElement?.nativeElement) throw new Error('The location map could not be opened. Try again.');
      map = new ArcMap({ basemap: 'gray-vector' });
      const assets = this.dxfVersion.assets.filter(asset => asset.kind === 'geojson' && asset.role === 'geometry');
      this.sourcePreviewFootprint = this.importImageCount > 0 || !assets.length || assets.length > 25 || this.dxfVersion.sublayers.reduce((sum, layer) => sum + layer.featureCount, 0) > 5000 || assets.reduce((sum, asset) => sum + asset.bytes, 0) > 2 * 1024 * 1024;
      if (this.sourcePreviewFootprint) {
        const layer = new GraphicsLayer({ opacity: this.sourcePreviewOpacity });
        for (const sublayer of this.dxfVersion.sublayers) {
          const [w, s, e, n] = this.publishingService.previewBounds(sublayer.bounds);
          layer.add(new Graphic({ geometry: { type: 'polygon', rings: [[[w, s], [w, n], [e, n], [e, s], [w, s]]], spatialReference: { wkid: 4326 } },
            symbol: { type: 'simple-fill', color: [16, 54, 214, 0.08], outline: { color: '#1036d6', width: 1, style: 'dash' } } }));
        }
        // A footprint preview never reads the features, so no field is offered.
        this.applyColorByCandidates(new Map());
        // The sheets go on the map first so the dashed outline draws over them.
        await this.addSourceSheetImages(map, generation);
        if (generation !== this.workGeneration) { map.destroy(); return; }
        map.add(layer);
      } else {
        const { grants } = await this.publishingService.sourcePreviewGrants(this.projectId, this.dxfVersion.id, assets.map(asset => asset.id), this.publicationAbort.signal);
        if (generation !== this.workGeneration) { map.destroy(); return; }
        this.requirePublication();
        const candidates = new Map<string, Set<string | number>>();
        const skipped = new Set<string>();
        const fieldNames = new Set<string>();
        // This pass re-reads every feature, so what an earlier pass learned
        // about the file's colours is replaced rather than added to.
        this.sourceColorMeta = new Map();
        this.sourcePreviewProperties = new Map();
        for (const asset of assets) {
          const grant = grants.find(item => item.assetId === asset.id);
          if (!grant?.url || new URL(grant.url).protocol !== 'https:') throw new Error('The private source preview is unavailable.');
          const bytes = await this.publishingService.readPreview(grant.url, this.publicationAbort.signal, asset);
          if (generation !== this.workGeneration) { map.destroy(); return; }
          this.requirePublication();
          const geojson = JSON.parse(new TextDecoder().decode(bytes));
          if (geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) throw new Error('The source preview is invalid.');
          // Split geometry families because an ArcGIS GeoJSONLayer supports one family.
          const families = new Map<string, unknown[]>();
          // Colouring by a field needs its values on the preview features, so the
          // colour-by candidates are collected before the features are rewritten.
          // Every feature then carries the same fields, because the SDK reads one
          // schema for the layer and would drop a field the first feature lacked.
          geojson.features.forEach(feature => {
            this.trackColorByValues(feature?.properties, candidates, skipped);
            this.trackFieldNames(feature?.properties, fieldNames);
            this.trackSourceColors(feature?.properties);
          });
          if (fieldNames.size) this.sourceFieldNames = [...fieldNames].sort((first, second) => first.localeCompare(second));
          // An id-like property would be adopted as the layer's object id and could
          // drop features when the source repeats a value, so it is left behind.
          const previewFields = [...candidates.keys()].filter(field => !/^(objectid|fid|oid)$/i.test(field));
          const add = (feature: { geometry: { type: string; geometries?: unknown[] }; properties?: unknown }, geometry = feature.geometry) => {
            if (geometry?.type === 'GeometryCollection') { geometry.geometries?.forEach(child => add(feature, child as typeof geometry)); return; }
            const family = { Point: 'point', MultiPoint: 'multipoint', LineString: 'polyline', MultiLineString: 'polyline', Polygon: 'polygon', MultiPolygon: 'polygon' }[geometry?.type];
            if (!family) return;
            if (!families.has(family)) families.set(family, []);
            // sn_index links the preview feature back to its full properties for tap-to-inspect.
            const index = this.sourcePreviewProperties.size;
            const sublayerKey = (asset as { sublayerKey?: string }).sublayerKey || '';
            this.sourcePreviewProperties.set(index, { sublayer: this.reviewLayers.find(row => row.key === sublayerKey)?.name || sublayerKey,
              properties: (feature?.properties && typeof feature.properties === 'object' ? feature.properties : {}) as Record<string, unknown> });
            families.get(family).push({ type: 'Feature', geometry, properties: { ...this.previewProperties(feature?.properties, previewFields), sn_index: index } });
          };
          geojson.features.forEach(feature => add(feature));
          for (const [family, features] of families) {
            const url = URL.createObjectURL(new Blob([JSON.stringify({ type: 'FeatureCollection', features })], { type: 'application/geo+json' }));
            this.sourcePreviewUrls.push(url);
            const layer = new GeoJSONLayer({ url, geometryType: family as __esri.GeoJSONLayerProperties['geometryType'], opacity: this.sourcePreviewOpacity,
              popupEnabled: false, outFields: ['*'] });
            map.add(layer); await layer.load();
            if (generation !== this.workGeneration) { map.destroy(); return; }
            this.requirePublication();
            this.sourcePreviewStyleLayers.push({ layer, family: family as MapSourcePreviewFamily });
            this.applySourcePreviewStyle(layer, family as MapSourcePreviewFamily);
          }
        }
        this.applyColorByCandidates(candidates);
        // The inventory may not have carried colours, and only this pass knows
        // which CAD layer or folder each one belongs to.
        this.applySourceColorDefaults();
        this.refreshSourceColorDefaults();
      }
      this.sourcePreviewView = new MapView({ container: this.sourcePreviewElement.nativeElement, map, ui: { components: ['zoom', 'attribution'] } });
      this.attachSourcePreviewClick(this.sourcePreviewView);
      await this.sourcePreviewView.when();
      if (generation !== this.workGeneration) { map.destroy(); return; }
      this.requirePublication(); await this.zoomPreview(true);
      this.sourcePreviewExpires = setTimeout(() => { this.destroySourcePreview(); this.sourcePreviewError = 'Location preview expired. Refresh it to continue.'; }, 570000);
    } catch (error) {
      map?.destroy();
      if (generation === this.workGeneration) {
        this.destroySourcePreview();
        if ([401, 403].includes(error?.status)) this.publicationFailure(error);
        else if (error?.name !== 'AbortError') this.sourcePreviewError = error?.message || 'Location preview could not load. Refresh it to try again.';
      }
    } finally { if (generation === this.workGeneration) this.sourcePreviewBusy = false; }
  }

  /**
   * Draw each imported raster sublayer's rasterised sheet over its footprint.
   *
   * The converter writes a downsampled RGBA PNG covering exactly the sublayer's
   * north-up bounds, so the bounds corners georeference it directly. Versions
   * imported before previews existed have no such asset and keep the footprint
   * outline alone.
   */
  private async addSourceSheetImages(map: __esri.Map, generation: number): Promise<void> {
    const sheets = this.dxfVersion.sublayers
      .filter(sublayer => sublayer.geometryTypes.includes('RASTER'))
      .map(sublayer => ({
        sublayer,
        asset: this.dxfVersion.assets.find(asset => asset.sublayerKey === sublayer.key
          && asset.kind === 'raster_preview' && asset.role === 'preview'),
      }))
      .filter((sheet): sheet is { sublayer: typeof sheet.sublayer; asset: ProjectMapSourceAsset } => !!sheet.asset);
    if (!sheets.length) return;
    let modules: [typeof import('@arcgis/core/layers/MediaLayer'), typeof import('@arcgis/core/layers/support/ImageElement'),
      typeof import('@arcgis/core/layers/support/CornersGeoreference'), typeof import('@arcgis/core/geometry/Point')];
    let grants: { assetId: string; url: string; expiresIn: number }[];
    try {
      modules = await Promise.all([
        import('@arcgis/core/layers/MediaLayer'), import('@arcgis/core/layers/support/ImageElement'),
        import('@arcgis/core/layers/support/CornersGeoreference'), import('@arcgis/core/geometry/Point'),
      ]);
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      ({ grants } = await this.publishingService.sourcePreviewGrants(
        this.projectId, this.dxfVersion.id, sheets.map(sheet => sheet.asset.id), this.publicationAbort.signal));
    } catch (error) {
      if (this.publishingService.previewFailureIsFatal(error)) throw error;
      console.warn('The imported sheet images could not be requested; their location outlines are shown instead.', error);
      return;
    }
    if (generation !== this.workGeneration) return;
    this.requirePublication();
    const [{ default: MediaLayer }, { default: ImageElement }, { default: CornersGeoreference }, { default: Point }] = modules;
    for (const sheet of sheets) {
      try {
        const grant = grants.find(item => item.assetId === sheet.asset.id);
        if (!grant?.url || new URL(grant.url).protocol !== 'https:') throw new Error('The private source preview is unavailable.');
        const bytes = await this.publishingService.readPreview(grant.url, this.publicationAbort.signal, sheet.asset);
        if (generation !== this.workGeneration) return;
        this.requirePublication();
        if (!bytes) throw new Error('The private source preview is unavailable.');
        // slice() detaches an exact-size ArrayBuffer, which Blob's typing requires.
        const url = URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: 'image/png' }));
        this.sourcePreviewUrls.push(url);
        const [west, south, east, north] = this.publishingService.previewBounds(sheet.sublayer.bounds);
        const corners = cornersToWebMercator({ ul: [west, north], ur: [east, north], lr: [east, south], ll: [west, south] });
        const point = ([x, y]: [number, number]) => new Point({ x, y, spatialReference: { wkid: 3857 } });
        const element = new ImageElement({
          image: url,
          georeference: new CornersGeoreference({
            topLeft: point(corners.topLeft), topRight: point(corners.topRight),
            bottomRight: point(corners.bottomRight), bottomLeft: point(corners.bottomLeft),
          }),
        });
        map.add(new MediaLayer({ title: sheet.sublayer.name, source: [element], opacity: this.sourcePreviewOpacity }));
        this.sourcePreviewSheetCount++;
      } catch (error) {
        // One unreadable sheet leaves that sublayer's footprint outline alone
        // rather than taking down a preview the others are still useful in.
        if (this.publishingService.previewFailureIsFatal(error)) throw error;
        console.warn(`The imported sheet for "${sheet.sublayer.name}" could not be shown; its location outline is shown instead.`, error);
      }
    }
  }

  /** Explains what the location preview is showing once it has loaded. */
  get sourcePreviewHint(): string {
    if (this.sourcePreviewSheetCount) {
      return this.sourcePreviewSheetCount < this.importImageCount
        ? 'Some imported sheets could not be shown, so their outlines are drawn instead. The rest show the imported sheet at reduced resolution; build the map preview to see the final tiles.'
        : 'The image shows the imported sheet at reduced resolution; build the map preview to see the final tiles.';
    }
    return this.importImageCount
      ? 'The outlines show the imported layer bounds. Build the map preview to see the packaged images and any features in their map location.'
      : "The outline shows this file's location bounds. Its geometry exceeds the inline preview limit; prepare the map preview below to review the actual features.";
  }

  /** The field the publisher chose to color by, or '' for a single layer color. */
  get colorByField(): string {
    return this.reviewSettings.colorBy?.field || '';
  }

  /**
   * The fields the picker offers. A field seeded from a published layer stays
   * listed even when this source preview did not offer it, so reopening the
   * layer never silently drops the colouring it was published with.
   */
  get colorByOptions(): string[] {
    const fields = this.fieldCandidates.map(item => item.field);
    const chosen = this.reviewSettings.colorBy?.field;
    const options = chosen && !fields.includes(chosen) ? [chosen, ...fields] : fields;
    if (!options.includes(SOURCE_COLOR_FIELD) && this.sourceColors().length > 1) options.unshift(SOURCE_COLOR_FIELD);
    // Keeping the file's own colours is the recommended choice, so it leads.
    return options.includes(SOURCE_COLOR_FIELD)
      ? [SOURCE_COLOR_FIELD, ...options.filter(field => field !== SOURCE_COLOR_FIELD)] : options;
  }

  /** What the picker calls a field. */
  colorByOptionLabel(field: string): string {
    return field === SOURCE_COLOR_FIELD ? SOURCE_COLOR_FIELD_LABEL : field;
  }

  /** The heading over the legend swatches; the file's own colours need no "Colors for". */
  get colorByLegendHeading(): string {
    const field = this.reviewSettings.colorBy?.field || '';
    return field === SOURCE_COLOR_FIELD ? SOURCE_COLOR_FIELD_LABEL : `Colors for ${field}`;
  }

  setColorByField(field: string): void {
    // Re-picking the current field would discard the colours already edited.
    if (field && field === this.reviewSettings.colorBy?.field) return;
    if (field === SOURCE_COLOR_FIELD) {
      const colors = this.sourceColors();
      this.reviewSettings.colorBy = colors.length ? this.sourceColorSelection(colors) : null;
      this.sourceColorsApplied = !!colors.length;
      this.publicationConfigurationChanged();
      return;
    }
    const candidate = this.fieldCandidates.find(item => item.field === field);
    this.reviewSettings.colorBy = candidate ? { field: candidate.field,
      values: candidate.values.map((value, index) => ({ value, color: MAP_VIEW_BY_COLORS[index % MAP_VIEW_BY_COLORS.length] })) } : null;
    this.publicationConfigurationChanged();
  }

  /**
   * The CAD layer or KML folder a feature belongs to, which is what a crew
   * recognises a colour by. DXF writes `Layer`, KML writes `Folder`.
   */
  private sourceGroupingName(properties: Record<string, unknown>): string {
    for (const key of Object.keys(properties)) {
      if (!/^(layer|folder)$/i.test(key)) continue;
      const value = properties[key];
      if (typeof value === 'string' && value.trim()) return value.trim().slice(0, 256);
    }
    return '';
  }

  /**
   * Record what the imported file drew each colour for, while the location
   * preview reads it: the layers or folders using the colour (first appearance
   * first), the polygon fill drawn with it, and how many features carry it.
   */
  private trackSourceColors(properties: unknown): void {
    if (!properties || typeof properties !== 'object') return;
    const record = properties as Record<string, unknown>;
    const color = sourceHexColor(record[SOURCE_COLOR_FIELD]);
    if (!color) return;
    const entry = this.sourceColorMeta.get(color)
      || { names: new Map<string, number>(), fills: new Map<string, number>(), count: 0 };
    entry.count++;
    const name = this.sourceGroupingName(record);
    if (name) entry.names.set(name, (entry.names.get(name) || 0) + 1);
    const fill = sourceHexColor(record[SOURCE_FILL_COLOR_FIELD]);
    if (fill) entry.fills.set(fill, (entry.fills.get(fill) || 0) + 1);
    this.sourceColorMeta.set(color, entry);
  }

  /**
   * The colours this file authored across the selected layers: what the
   * converter inventoried, plus anything the location preview itself read, so
   * a version imported before the inventory carried colours still works.
   */
  private sourceColors(): string[] {
    const colors: string[] = [];
    const add = (value: unknown) => {
      const color = sourceHexColor(value);
      if (color && !colors.includes(color) && colors.length < MAX_SOURCE_COLOR_VALUES + 1) colors.push(color);
    };
    for (const row of this.reviewLayers.filter(layer => layer.selected)) {
      (this.dxfVersion?.sublayers.find(layer => layer.key === row.key)?.sourceColors || []).forEach(add);
    }
    this.sourceColorMeta.forEach((_meta, color) => add(color));
    (this.fieldCandidates.find(item => item.field === SOURCE_COLOR_FIELD)?.values || []).forEach(add);
    return colors;
  }

  /** What the legend calls one of the file's colours. */
  private legendLabel(color: string): string {
    return sourceColorLabel(color, [...(this.sourceColorMeta.get(color)?.names.keys() || [])]);
  }

  /** The fill the file drew most often behind this pen colour, if any. */
  private sourceFillColor(color: string): string | undefined {
    const fills = [...(this.sourceColorMeta.get(color)?.fills.entries() || [])].sort((first, second) => second[1] - first[1]);
    return fills.length && fills[0][0] !== color ? fills[0][0] : undefined;
  }

  /** Every distinct colour the file holds, even beyond what the inventory lists. */
  private sourceColorTotal(colors: string[]): number {
    const counts = this.reviewLayers.filter(layer => layer.selected).map(row =>
      Number(this.dxfVersion?.sublayers.find(item => item.key === row.key)?.sourceColorCount) || 0);
    return Math.max(colors.length, ...counts, 0);
  }

  /** The colour the most features were drawn in, for the too-many-colours fallback. */
  private dominantSourceColor(colors: string[]): string {
    const counted = colors.map(color => ({ color, count: this.sourceColorMeta.get(color)?.count || 0 }))
      .sort((first, second) => second.count - first.count);
    return counted[0]?.color || this.reviewSettings.color;
  }

  /**
   * A colour-by selection that paints each of the file's colours as itself.
   * `sourceColor` is the pen the file drew with, and a polygon that also named
   * a brush fills with that instead while keeping the pen for its outline.
   */
  private sourceColorSelection(colors: string[]): MapReviewColorBy {
    return { field: SOURCE_COLOR_FIELD, values: colors.map(color => {
      const fillColor = this.sourceFillColor(color);
      return { value: color, color, label: this.legendLabel(color), ...(fillColor ? { fillColor } : {}) };
    }) };
  }

  /** The most symbol families any selected layer needs, for the rendition budget. */
  private maxGeometryFamilies(): number {
    const counts = this.reviewLayers.filter(layer => layer.selected).map(row =>
      this.publishingService.geometryFamilies(this.dxfVersion?.sublayers.find(layer => layer.key === row.key)?.geometryTypes || []).length);
    return counts.length ? Math.max(...counts) : 3;
  }

  /**
   * Start a fresh review in the colours the file was drawn in, so an imported
   * DXF or KML looks on the map like it looks in CAD. One colour needs no
   * legend and becomes the layer colour; a file with more colours than one
   * prepared layer can carry falls back to its most common colour.
   *
   * A published layer or a saved draft has already seeded its own style, and a
   * reviewer's own choice always wins, so neither is overwritten.
   */
  private applySourceColorDefaults(): void {
    if (this.sourceColorsApplied || this.reviewSeeded || this.reviewDirty || this.reviewSettings.colorBy) return;
    if (!this.hasSelectedVectors) return;
    const colors = this.sourceColors();
    const chosen = sourceColorDefault({ colors, total: this.sourceColorTotal(colors),
      families: this.maxGeometryFamilies(), symbolBudget: MAX_UNIQUE_VALUE_SYMBOLS,
      dominant: this.dominantSourceColor(colors) });
    if (chosen.kind === 'none') return;
    this.sourceColorsApplied = true;
    if (chosen.kind === 'color-by') this.reviewSettings.colorBy = this.sourceColorSelection(chosen.colors);
    else this.reviewSettings.color = chosen.color;
    this.sourceColorHint = chosen.kind === 'dominant-color' ? chosen.hint : '';
    this.applySeededStyle();
  }

  /**
   * Finish the seeded defaults once the location preview has read the source
   * itself. The inventory gives colours but no names for them, and no feature
   * counts to say which colour a too-colourful file should keep.
   */
  private refreshSourceColorDefaults(): void {
    if (!this.sourceColorsApplied || this.reviewDirty) return;
    if (this.sourceColorHint) {
      const dominant = this.dominantSourceColor(this.sourceColors());
      if (dominant && dominant !== this.reviewSettings.color) {
        this.reviewSettings.color = dominant;
        this.applySeededStyle();
      }
      return;
    }
    if (this.reviewSettings.colorBy?.field !== SOURCE_COLOR_FIELD) return;
    let changed = false;
    for (const entry of this.reviewSettings.colorBy.values) {
      const color = sourceHexColor(entry.value);
      if (!color) continue;
      const label = this.legendLabel(color);
      const fillColor = this.sourceFillColor(color);
      if (label !== entry.label) { entry.label = label; changed = true; }
      if (fillColor !== entry.fillColor) { entry.fillColor = fillColor; changed = true; }
    }
    if (changed) this.applySeededStyle();
  }

  /**
   * Apply a style the drawer chose for the reviewer rather than one they typed:
   * the prepared output is rebuilt, but the review is not marked as edited and
   * a layer already on the map does not look like it has unsaved changes.
   */
  private applySeededStyle(): void {
    const dirty = this.reviewDirty;
    const hint = this.sourceColorHint;
    const baseline = this.reviewBaseline;
    this.publicationConfigurationChanged();
    this.reviewDirty = dirty;
    this.sourceColorHint = hint;
    if (baseline && !dirty) this.reviewBaseline = this.publishFingerprint();
  }

  /**
   * Record the distinct values of each source property so the publisher can
   * color by one. Only text and finite numbers can be matched by the renderer,
   * and a field with more than MAX_COLOR_BY_VALUES distinct values makes an
   * unreadable legend, so both are dropped for good rather than truncated.
   */
  private trackColorByValues(properties: unknown, candidates: Map<string, Set<string | number>>, skipped: Set<string>): void {
    if (!properties || typeof properties !== 'object') return;
    for (const [field, value] of Object.entries(properties as Record<string, unknown>)) {
      // `sourceFillColor` only qualifies a stroke colour, so it is never a
      // choice of its own; `sourceColor` is, and carries more values.
      if (skipped.has(field) || field === 'sourceStyle' || field === SOURCE_FILL_COLOR_FIELD
        || field === 'id' || field.length > 256) continue;
      if (value === null || value === undefined || value === '') continue;
      const usable = (typeof value === 'string' && value.length <= 256)
        || (typeof value === 'number' && Number.isFinite(value));
      if (!usable) { skipped.add(field); candidates.delete(field); continue; }
      const values = candidates.get(field) || new Set<string | number>();
      values.add(value as string | number);
      const cap = field === SOURCE_COLOR_FIELD ? MAX_SOURCE_COLOR_VALUES : MAX_COLOR_BY_VALUES;
      if (values.size > cap) { skipped.add(field); candidates.delete(field); continue; }
      candidates.set(field, values);
    }
  }

  private applyColorByCandidates(candidates: Map<string, Set<string | number>>): void {
    this.fieldCandidates = [...candidates.entries()]
      .map(([field, values]) => ({ field, values: [...values].sort((first, second) =>
        typeof first === 'number' && typeof second === 'number' ? first - second : String(first).localeCompare(String(second))) }))
      .sort((first, second) => first.field.localeCompare(second.field));
    // A refreshed source may no longer carry the chosen field. An empty list
    // says nothing about the source (a footprint preview reads no features),
    // so only a preview that found fields can clear the selection. The file's
    // own colours are exempt: a file with more of them than the colour-by cap
    // allows drops off this list while still being exactly what to colour by.
    if (this.fieldCandidates.length && this.reviewSettings.colorBy
      && !(this.sourceColorsApplied && this.reviewSettings.colorBy.field === SOURCE_COLOR_FIELD)
      && !this.fieldCandidates.some(item => item.field === this.reviewSettings.colorBy.field)) {
      this.setColorByField('');
    }
  }

  async zoomPreview(source: boolean): Promise<void> {
    const view = source ? this.sourcePreviewView : this.previewView;
    if (!view) return;
    if (source && this.sourceType === 'arcgis_url') {
      if (this.livePreviewExtent && !this.livePreviewAbort?.signal.aborted) {
        await view.goTo(this.livePreviewExtent.clone().expand(1.15), { animate: false });
      }
      return;
    }
    if (!this.currentPublication()) return;
    let bounds: [number, number, number, number][];
    try { bounds = (source ? this.dxfVersion.sublayers : this.preparedRenditions).map(item => this.publishingService.previewBounds(item.bounds)); }
    catch (error) {
      if (source) { this.destroySourcePreview(); this.sourcePreviewError = error.message; }
      else { this.destroyPublicationPreview(); this.publicationError = error.message; }
      return;
    }
    if (!bounds.length) return;
    const [{ default: Extent }] = await Promise.all([import('@arcgis/core/geometry/Extent')]);
    if (!this.currentPublication() || view.destroyed) return;
    await view.goTo(new Extent({ xmin: Math.min(...bounds.map(item => item[0])), ymin: Math.min(...bounds.map(item => item[1])),
      xmax: Math.max(...bounds.map(item => item[2])), ymax: Math.max(...bounds.map(item => item[3])), spatialReference: { wkid: 4326 } }).expand(1.15), { animate: false });
  }

  setReviewOpacity(field: 'fillAlpha' | 'defaultOpacity', percent: number): void {
    this.reviewSettings[field] = percent === null ? NaN : percent / 100;
    if (field === 'fillAlpha') this.publicationConfigurationChanged();
    else this.reviewed = false;
  }

  setPreviewPercent(source: boolean, percent: number): void {
    if (source) { this.sourcePreviewOpacity = percent / 100; this.sourcePreviewOpacityChosen = true; }
    else { this.previewOpacity = percent / 100; this.previewOpacityChosen = true; }
    this.setPreviewOpacity(source);
  }

  setPreviewOpacity(source: boolean): void {
    const view = source ? this.sourcePreviewView : this.previewView;
    view?.map.layers.forEach(layer => { layer.opacity = source ? this.sourcePreviewOpacity : this.previewOpacity; });
  }

  /**
   * Draw the location preview with the appearance chosen below it, so a colour,
   * width, size or colour-by change shows without refetching the source.
   * Called with a layer while the preview is being built, and without one when
   * a setting changed.
   */
  private applySourcePreviewStyle(layer?: __esri.GeoJSONLayer, family?: MapSourcePreviewFamily): void {
    const targets = layer && family ? [{ layer, family }] : this.sourcePreviewStyleLayers;
    for (const target of targets) {
      if (target.layer.destroyed) continue;
      target.layer.renderer = mapSourcePreviewRenderer(this.reviewSettings, target.family,
        this.previewColorByField(target.layer)) as unknown as __esri.Renderer;
    }
  }

  /**
   * The chosen colour-by field as this preview layer names it, or null when the
   * layer has no such field — a renderer matching a missing field draws nothing,
   * so those features keep the plain layer colour instead.
   */
  private previewColorByField(layer: __esri.GeoJSONLayer): string | null {
    const field = this.reviewSettings.colorBy?.field?.trim();
    if (!field) return null;
    const fields = layer.fields || [];
    const match = fields.find(item => item.name === field)
      || fields.find(item => item.name?.toLowerCase() === field.toLowerCase());
    return match ? match.name : null;
  }

  /**
   * The scalar properties a preview feature keeps. Only the colour-by candidates
   * are carried over; anything else would add weight the preview never reads.
   */
  private previewProperties(properties: unknown, fields: string[]): Record<string, string | number | null> {
    const source = properties && typeof properties === 'object' ? properties as Record<string, unknown> : {};
    const kept: Record<string, string | number | null> = {};
    for (const field of fields) {
      const value = source[field];
      kept[field] = (typeof value === 'string' && value.length <= 256) || (typeof value === 'number' && Number.isFinite(value))
        ? value as string | number : null;
    }
    return kept;
  }

  previewSatelliteOn(source: boolean): boolean {
    return source ? this.sourcePreviewSatellite : this.previewSatellite;
  }

  /**
   * Switch this preview between the default basemap and satellite imagery.
   * The choice belongs to the open map only: it is never published and never
   * touches the reviewer's own project-map basemap.
   */
  async togglePreviewBasemap(source: boolean): Promise<void> {
    const view = source ? this.sourcePreviewView : this.previewView;
    if (!view || view.destroyed || !view.map) return;
    const satellite = !this.previewSatelliteOn(source);
    const { default: Basemap } = await import('@arcgis/core/Basemap');
    const current = source ? this.sourcePreviewView : this.previewView;
    if (current !== view || view.destroyed || !view.map) return;
    view.map.basemap = Basemap.fromId(satellite ? PREVIEW_SATELLITE_BASEMAP_ID : PREVIEW_BASEMAP_ID);
    if (source) this.sourcePreviewSatellite = satellite; else this.previewSatellite = satellite;
    // Imagery is busy behind a solid layer; ease it back unless the reviewer
    // already chose an opacity for this preview.
    const chosen = source ? this.sourcePreviewOpacityChosen : this.previewOpacityChosen;
    if (satellite && !chosen) {
      if (source) this.sourcePreviewOpacity = PREVIEW_SATELLITE_OPACITY; else this.previewOpacity = PREVIEW_SATELLITE_OPACITY;
      this.setPreviewOpacity(source);
    }
  }

  zoomPreviewBy(source: boolean, levels: number): void {
    const view = source ? this.sourcePreviewView : this.previewView;
    if (!view || view.destroyed) return;
    void view.goTo({ zoom: view.zoom + levels }, { duration: 300 }).catch(() => undefined);
  }

  private previewShell(source: boolean): HTMLElement | undefined {
    return (source ? this.sourcePreviewShellElement : this.previewShellElement)?.nativeElement;
  }

  togglePreviewExpanded(source: boolean): void {
    const shell = this.previewShell(source);
    if (!shell) return;
    if (document.fullscreenElement === shell) { void document.exitFullscreen(); return; }
    if (shell.requestFullscreen) {
      void shell.requestFullscreen().catch(() => { this.publicationError = 'Expanded map is unavailable in this browser. Use the inline map controls.'; });
    } else if (source) this.sourcePreviewExpanded = !this.sourcePreviewExpanded;
    else this.previewExpanded = !this.previewExpanded;
  }

  private exitPreviewFullscreen(source: boolean): void {
    const shell = this.previewShell(source);
    if (shell && document.fullscreenElement === shell) void document.exitFullscreen().catch(() => undefined);
  }

  async reloadPublicationCatalog(): Promise<void> {
    if (this.publicationBusy || !this.currentPublication()) return;
    this.publicationBusy = true;
    const generation = this.workGeneration;
    try {
      const catalog = await this.publishingService.catalog(this.projectId, this.publicationAbort.signal);
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      this.publicationCatalog = catalog;
      this.publicationConflict = false;
      this.publicationError = '';
      this.reviewed = false;
      if (this.groupId && !catalog?.groups.some(group => group.id === this.groupId)) this.groupId = null;
    } catch (error) { if (generation === this.workGeneration) this.publicationFailure(error); }
    finally { if (generation === this.workGeneration) this.publicationBusy = false; }
  }

  async publishReviewedLayers(): Promise<void> {
    const reusePublished = this.reusesPublishedRenditions;
    if ((!this.skipsReviewConfirmation && (!this.reviewed || !this.previewReady)) || (!this.preparedRenditions.length && !reusePublished)
      || this.publicationBusy || this.publicationConflict || !this.currentPublication()) return;
    if (!this.publishingService.canPublish(this.projectId)) { this.publicationFailure({ status: 403 }); return; }
    const group = this.groupId === null ? null : this.publicationCatalog?.groups.find(item => item.id === this.groupId)
      || { id: this.newGroupId, name: this.newGroupName.trim(), order: Math.max(0, ...(this.publicationCatalog?.groups.map(item => item.order + 1) || [])) };
    if ((group && (!group.name || group.name.length > 256)) || !Number.isInteger(this.layerOrder) || this.layerOrder < 0
      || this.layerOrder + this.reviewLayers.length > 2147483647
      || !Number.isFinite(this.reviewSettings.defaultOpacity) || this.reviewSettings.defaultOpacity < 0 || this.reviewSettings.defaultOpacity > 1) {
      this.publicationError = 'Choose a group name and a non-negative layer order.'; return;
    }
    this.publishing = true;
    this.publicationBusy = true;
    this.publicationError = '';
    const generation = this.workGeneration;
    try {
      const entries: MapPublicationEntry[] = [];
      for (const [index, row] of this.reviewLayers.filter(item => item.selected).entries()) {
        this.requirePublication();
        const display = await this.publishingService.display(this.projectId, this.dxfVersion.id, row, this.publicationAbort.signal);
        if (generation !== this.workGeneration) return;
        this.requirePublication();
        const published = reusePublished ? this.publishedRenditionsFor(row.key) : null;
        entries.push({ id: display.id, sourceVersionId: this.dxfVersion.id, sublayerKey: row.key,
          renditionIds: published ? published.renditionIds : this.preparedRenditions.filter(asset => asset.sublayerKey === row.key).map(asset => asset.id),
          name: row.name.trim(), groupId: group?.id ?? null, order: this.layerOrder + index,
          defaultVisible: this.reviewSettings.defaultVisible, defaultOpacity: this.reviewSettings.defaultOpacity,
          labelDefault: this.publicationCatalog?.layers.find(layer => layer.id === display.id)?.labelDefault ?? false,
          ...(published ? { minZoom: published.minZoom, maxZoom: published.maxZoom } : this.preparedZoomRange(row.key)) });
      }
      const result = await this.publishingService.publish(this.projectId,
        this.publishingService.publication(this.publicationCatalog, entries, group), this.publicationAbort.signal);
      if (generation !== this.workGeneration) return;
      this.requirePublication();
      this.publicationRevision = result.catalogRevision;
      this.publicationPhase = 'Saved to the project map. These layers are now available in web and mobile.';
      // What is on the map now matches the drawer, so there is nothing left to save.
      this.reviewDirty = false;
      this.reviewBaseline = this.publishFingerprint();
      this.sourceService.listChanged.next();
      this.refLayerService.listChanged.next();
      this.destroyPublicationPreview();
      // Reload the version so its status reads "On the map" and the one-step Add to map button withdraws.
      this.startPolling(this.dxfVersion.id);
    } catch (error) {
      if (generation !== this.workGeneration) return;
      this.publicationConflict = error?.status === 409;
      this.publicationFailure(error);
    } finally { if (generation === this.workGeneration) { this.publicationBusy = false; this.publishing = false; } }
  }

  /**
   * The drawer's single primary action. A stale or missing preview is built
   * first; an import that still needs the reviewer's confirmation stops there
   * with the freshly opened preview to check.
   */
  async addToMap(): Promise<void> {
    if (!this.canAddToMap) return;
    if (!this.preparedRenditions.length && !this.reusesPublishedRenditions) {
      await this.preparePublication();
      if (!this.preparedRenditions.length || (!this.skipsReviewConfirmation && !this.reviewed)) return;
    }
    await this.publishReviewedLayers();
  }

  /** Rail refresh: re-read the source, or re-open the prepared map. */
  refreshPreview(source: boolean): void {
    if (!source) { void this.previewPublication(); return; }
    if (this.sourceType === 'arcgis_url' && this.arcgisMode === 'live') { void this.showLivePreview(); return; }
    void this.showSourcePreview();
  }

  private publicationFailure(error: { status?: number; name?: string; message?: string }): void {
    if (error?.name === 'AbortError' || this.publicationAbort.signal.aborted) return;
    if ([401, 403].includes(error?.status)) {
      this.destroySourcePreview();
      this.destroyPublicationPreview();
      this.preparedRenditions = [];
      this.publicationLoaded = false;
      this.publicationError = 'Your account no longer has access to manage these project layers.';
    } else if (error?.status === 409) {
      this.preparationNeedsRetry = !this.publicationConflict;
      this.publicationError = this.publicationConflict ? 'The project catalog changed. Reload it, review the latest catalog, and publish again.'
        : 'The preparation request changed. Start a new preparation attempt.';
    } else this.publicationError = error?.message || 'This action could not be completed. Check your connection and try again.';
  }

  cancelImport(): void {
    if (!this.importIsProcessing || this.submitting) return;
    this.pollRequest?.unsubscribe();
    this.submitting = true;
    this.importPhase = 'Cancelling import…';
    const generation = this.workGeneration;
    this.dxfRequest = this.sourceService.cancel(this.projectId, this.dxfVersion.id).pipe(
      takeUntil(this.workCancelled), takeUntil(this.onDestroy),
      finalize(() => { if (generation === this.workGeneration) this.submitting = false; }),
    ).subscribe({
      next: version => {
        if (generation !== this.workGeneration) return;
        this.importPhase = '';
        this.acceptVersion(version);
      },
      error: error => {
        if (generation !== this.workGeneration) return;
        this.onSubmitError(error);
        this.pollingError = true;
        if (error.status === 409) this.refreshImportStatus();
      },
    });
  }

  private onSubmitSuccess(): void {
    this.submitting = false;
    this.refLayerService.listChanged.next();
    this.projectsService.closeRightPanel.next();
  }

  private onSubmitError(err: any): void {
    this.submitting = false;
    this.importPhase = '';
    if (err?.status === 0 && this.isDesignImport) {
      this.submitError = 'Unable to connect. Check your connection and try again.';
      return;
    }
    if (err?.status === 503 && this.isDesignImport) {
      this.submitError = 'Design imports are not configured for this environment. Contact your project administrator.';
      return;
    }
    this.submitError =
      err?.error?.error ||
      err?.error?.message ||
      err?.message ||
      (this.isEdit ? 'Failed to save layer' : 'Failed to create layer');
  }
}
